import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Gift, Users, Crown, Star, Settings, Send, RefreshCw, Search,
  ArrowLeft, Phone, Mail, Award, TrendingUp, Calendar, Percent,
  Euro, Ticket, Printer, Eye, RotateCcw, X, Check, Sparkles,
  MessageSquare, Clock, ChevronRight, UserPlus, Zap, Coins,
  ShieldCheck, BarChart3, AlertTriangle, Plus, Minus, Pencil,
  History, Wrench, ShoppingBag, FileText, Save, Trash2, Info,
  HelpCircle, BookOpen, Wallet, Bell, Target, UserX, Play, User, Store
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catégories de produits
const PRODUCT_CATEGORIES = {
  reparation: { label: "Réparation", color: "bg-blue-100 text-blue-700" },
  accessoire: { label: "Accessoire", color: "bg-green-100 text-green-700" },
  occasion: { label: "Téléphone d'occasion", color: "bg-orange-100 text-orange-700" },
  neuf: { label: "Téléphone neuf", color: "bg-purple-100 text-purple-700" },
  dematerialise: { label: "Vente dématérialisée", color: "bg-gray-100 text-gray-500" },
  autre: { label: "Autre", color: "bg-slate-100 text-slate-700" }
};

export default function LoyaltyPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [pointsReport, setPointsReport] = useState(null);
  
  // Clients
  const [clients, setClients] = useState([]);
  const [clientsFilter, setClientsFilter] = useState('all');
  const [clientsSearch, setClientsSearch] = useState('');
  const [debouncedClientsSearch, setDebouncedClientsSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState(null);
  const [clientPoints, setClientPoints] = useState(null);
  
  // Promos
  const [promos, setPromos] = useState([]);
  const [promosFilter, setPromosFilter] = useState('all');
  
  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsChanged, setSettingsChanged] = useState(false);
  
  // Consommation / Suivi
  const [consumptionDashboard, setConsumptionDashboard] = useState(null);
  const [consumptionPeriod, setConsumptionPeriod] = useState('month');
  const [avoirs, setAvoirs] = useState([]);
  const [showAvoirModal, setShowAvoirModal] = useState(false);
  const [newAvoir, setNewAvoir] = useState({
    client_id: '',
    amount: 0,
    reason: '',
    deduct_points: true
  });
  
  // Reporting Dashboard (Burn Rate, Stock Points, Panier Moyen)
  const [reportingDashboard, setReportingDashboard] = useState(null);
  const [reportingPeriod, setReportingPeriod] = useState('month');
  
  // Modals
  const [showNewPromoModal, setShowNewPromoModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showPromoCardModal, setShowPromoCardModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showAdjustPointsModal, setShowAdjustPointsModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoCardHtml, setPromoCardHtml] = useState('');
  
  // Points adjustment
  const [adjustPoints, setAdjustPoints] = useState({ points: 0, notes: '' });
  
  // ===== AUDIT & NETTOYAGE =====
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  
  // ===== RESET OTP SÉCURISÉ =====
  const [showResetOtpModal, setShowResetOtpModal] = useState(false);
  const [resetOtpEmail, setResetOtpEmail] = useState('ouahcen397@yahoo.fr');
  const [resetOtpCode, setResetOtpCode] = useState('');
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetOtpLoading, setResetOtpLoading] = useState(false);
  
  // ===== RECALCUL DES POINTS =====
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [recalculateResult, setRecalculateResult] = useState(null);
  
  // ===== BACKUPS & RESTAURATION =====
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [showBackupsModal, setShowBackupsModal] = useState(false);
  
  // ===== RATTRAPAGE DES POINTS =====
  const [rattrapageLoading, setRattrapageLoading] = useState(false);
  const [rattrapageResult, setRattrapageResult] = useState(null);
  const [initLoading, setInitLoading] = useState(false);
  
  // ===== BLOC A - Gestion Clients =====
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [showManualActionModal, setShowManualActionModal] = useState(false);
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [clientModificationHistory, setClientModificationHistory] = useState([]);
  
  // Nouveau client
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    loyalty_tier: 'standard',
    sms_marketing: true,  // Activé par défaut pour notification bienvenue
    email_marketing: true  // Activé par défaut pour notification bienvenue
  });
  
  // Édition client
  const [editClient, setEditClient] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    loyalty_tier: 'standard',
    sms_marketing: false,
    email_marketing: false,
    reason: ''
  });
  
  // Action manuelle
  const [manualAction, setManualAction] = useState({
    action_type: 'purchase',
    amount: 0,
    category: 'autre',
    description: '',
    reason: '',
    add_visit: true,
    calculate_points: true
  });
  
  // Recherche client rapide pour promo
  const [promoClientSearch, setPromoClientSearch] = useState('');
  const [filteredClientsForPromo, setFilteredClientsForPromo] = useState([]);
  
  // ===== BLOC B - Promotions Avancées =====
  const [showMassPromoModal, setShowMassPromoModal] = useState(false);
  const [massPromoClients, setMassPromoClients] = useState([]); // Clients sélectionnés pour promo de masse
  const [massPromoSelectAll, setMassPromoSelectAll] = useState(false);
  const [massPromoFilter, setMassPromoFilter] = useState('all'); // all, bronze, silver, gold
  const [massPromo, setMassPromo] = useState({
    promo_type: 'percent',
    value: 10,
    description: '',
    validity_days: 30,
    send_sms: true,
    send_email: true
  });
  
  // ===== BLOC C - Génération IA Promo =====
  const [showAIPromoModal, setShowAIPromoModal] = useState(false);
  const [aiPromoKeywords, setAiPromoKeywords] = useState('');
  const [aiPromoGenerating, setAiPromoGenerating] = useState(false);
  const [aiPromoResult, setAiPromoResult] = useState(null);
  const [aiPromoApproved, setAiPromoApproved] = useState(false);
  
  // ===== Modal Information Système =====
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  // ===== NOTIFICATIONS CLIENT =====
  const [notificationSettings, setNotificationSettings] = useState({
    monthly_recap_enabled: true,
    monthly_recap_day: 1,
    smart_incentives_enabled: true,
    incentive_threshold_near: 20,
    incentive_threshold_ready: 100,
    prefer_email: true
  });
  const [clientsToNotify, setClientsToNotify] = useState({
    near_threshold: [],
    ready_to_redeem: [],
    inactive: []
  });
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [relanceClient, setRelanceClient] = useState(null);
  const [relanceMessage, setRelanceMessage] = useState('');
  
  // Modal de confirmation avant envoi
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false);
  const [confirmSendType, setConfirmSendType] = useState(null); // 'near_threshold', 'ready_to_redeem', 'monthly_recap'
  const [confirmSendClients, setConfirmSendClients] = useState([]);
  
  // Modal de consommation de points
  const [showConsumePointsModal, setShowConsumePointsModal] = useState(false);
  const [consumePointsClient, setConsumePointsClient] = useState(null);
  const [consumePointsData, setConsumePointsData] = useState({
    points_to_use: 100,
    redemption_type: 'fixed',
    sale_amount: 0,
    category: 'autre'
  });
  
  // New promo form
  const [newPromo, setNewPromo] = useState({
    client_id: '',
    promo_type: 'percent',
    value: 10,
    description: '',
    validity_days: 30,
    max_uses: 1,
    send_sms: false,
    send_email: true,
    scope: 'individual'  // 'individual' ou 'general'
  });
  
  // Campaign form
  const [campaign, setCampaign] = useState({
    tier: 'all',
    promo_type: 'percent',
    value: 10,
    message: '',
    send_sms: false,  // Par défaut désactivé - l'admin active manuellement
    send_email: true,  // Email par défaut
    selectedClients: []  // Pour sélection manuelle
  });
  
  // État pour le modal de sélection de clients pour campagne
  const [showCampaignClientSelector, setShowCampaignClientSelector] = useState(false);
  const [campaignClientSearch, setCampaignClientSearch] = useState('');
  
  // ===== GÉNÉRATION IA MESSAGE CAMPAGNE =====
  const [campaignAIGenerating, setCampaignAIGenerating] = useState(false);
  const [campaignAIResult, setCampaignAIResult] = useState(null);
  
  // ===== SYNCHRONISATION COMPLÈTE & VALIDATIONS EN ATTENTE =====
  const [syncingFull, setSyncingFull] = useState(false);
  const [syncFullResult, setSyncFullResult] = useState(null);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [showPendingValidationsModal, setShowPendingValidationsModal] = useState(false);
  const [processingValidation, setProcessingValidation] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/dashboard`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    }
  }, [getAuthHeaders]);

  // Load clients
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsHasMore, setClientsHasMore] = useState(false);
  const [clientsSkip, setClientsSkip] = useState(0);
  const CLIENTS_LIMIT = 50;

  const loadClients = useCallback(async (append = false) => {
    try {
      const params = new URLSearchParams();
      if (clientsFilter !== 'all') params.append('tier', clientsFilter);
      if (debouncedClientsSearch) params.append('search', debouncedClientsSearch);
      params.append('limit', String(CLIENTS_LIMIT));
      params.append('skip', String(append ? clientsSkip : 0));
      
      const res = await fetch(`${API_URL}/api/loyalty/clients?${params}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setClients(prev => [...prev, ...(data.clients || [])]);
        } else {
          setClients(data.clients || []);
        }
        setClientsTotal(data.total || 0);
        setClientsHasMore(data.has_more || false);
        setClientsSkip(data.skip + (data.clients?.length || 0));
      }
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  }, [clientsFilter, debouncedClientsSearch, clientsSkip, getAuthHeaders]);

  const loadMoreClients = async () => {
    if (clientsHasMore) {
      await loadClients(true);
    }
  };

  // Load promos
  const loadPromos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (promosFilter !== 'all') params.append('status', promosFilter);
      
      const res = await fetch(`${API_URL}/api/loyalty/promos?${params}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setPromos(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement promos:', error);
    }
  }, [promosFilter, getAuthHeaders]);

  // Initial load - SANS loadClients dans les deps pour éviter les rechargements
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadPromos(), loadPendingValidations()]);
      // loadClients sera appelé par l'effet suivant avec debouncedClientsSearch
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDashboard, loadPromos]);

  // Debounce pour la recherche clients (500ms pour éviter trop de requêtes)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClientsSearch(clientsSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [clientsSearch]);

  // Reload clients when filter or debounced search changes
  // Ce useEffect gère TOUS les chargements de clients (initial + recherche)
  useEffect(() => {
    setClientsSkip(0); // Reset pagination
    loadClients(false); // Load fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsFilter, debouncedClientsSearch]);

  // Reload promos when filter changes
  useEffect(() => {
    loadPromos();
  }, [promosFilter, loadPromos]);

  // Sync all clients
  const handleSyncClients = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/sync-clients`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await loadDashboard();
        await loadClients();
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setSyncing(false);
  };

  // Update loyalty status
  const handleUpdateLoyaltyStatus = async () => {
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/update-loyalty-status`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await loadDashboard();
        await loadClients();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setUpdatingStatus(false);
  };

  // Load points report
  const loadPointsReport = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/report/points?period=month`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setPointsReport(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement rapport points:', error);
    }
  }, [getAuthHeaders]);

  // Load consumption dashboard
  const loadConsumptionDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/consumption/dashboard?period=${consumptionPeriod}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setConsumptionDashboard(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement consommation:', error);
    }
  }, [getAuthHeaders, consumptionPeriod]);

  // Load avoirs
  const loadAvoirs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/avoirs?include_used=true`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setAvoirs(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement avoirs:', error);
    }
  }, [getAuthHeaders]);

  // Load reporting dashboard (Burn Rate, Stock Points, Panier Moyen)
  const loadReportingDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/reporting/dashboard?period=${reportingPeriod}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setReportingDashboard(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement reporting:', error);
    }
  }, [getAuthHeaders, reportingPeriod]);

  // Create avoir
  const handleCreateAvoir = async () => {
    if (!newAvoir.client_id || newAvoir.amount <= 0) {
      toast.error('Sélectionnez un client et entrez un montant');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('client_id', newAvoir.client_id);
      params.append('amount', newAvoir.amount);
      params.append('reason', newAvoir.reason || 'Avoir créé');
      params.append('deduct_points', newAvoir.deduct_points);
      
      const res = await fetch(`${API_URL}/api/loyalty/avoir/create?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowAvoirModal(false);
        setNewAvoir({ client_id: '', amount: 0, reason: '', deduct_points: true });
        await loadAvoirs();
        await loadClients();
        await loadConsumptionDashboard();
      } else {
        toast.error('Erreur lors de la création de l\'avoir');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete avoir
  const handleDeleteAvoir = async (avoirId) => {
    if (!confirm('Supprimer cet avoir ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/avoirs/${avoirId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Avoir supprimé');
        await loadAvoirs();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete client (permanent)
  const handleDeleteClient = async (clientId, permanent = false) => {
    const message = permanent 
      ? 'Supprimer définitivement ce client et toutes ses données ? Cette action est irréversible.'
      : 'Archiver ce client ?';
    
    if (!confirm(message)) return;
    
    try {
      const endpoint = permanent 
        ? `${API_URL}/api/loyalty/clients/${clientId}/permanent`
        : `${API_URL}/api/loyalty/clients/${clientId}`;
      
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success(permanent ? 'Client supprimé définitivement' : 'Client archivé');
        setShowEditClientModal(false);
        await loadClients();
        await loadDashboard();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete promo
  const handleDeletePromo = async (promoId) => {
    if (!confirm('Supprimer cette promotion ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/promos/${promoId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Promotion supprimée');
        await loadPromos();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Load client points
  const loadClientPoints = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/points/${clientId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setClientPoints(await res.json());
      }
    } catch (error) {
      console.error('Erreur chargement points client:', error);
    }
  };

  // Adjust points
  const handleAdjustPoints = async () => {
    if (!selectedClient || adjustPoints.points === 0) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/points/adjust`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          client_id: selectedClient.id,
          transaction_type: 'adjust',
          points: adjustPoints.points,
          notes: adjustPoints.notes || 'Ajustement manuel'
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowAdjustPointsModal(false);
        setAdjustPoints({ points: 0, notes: '' });
        await loadClientPoints(selectedClient.id);
        await loadClients();
      } else {
        toast.error('Erreur lors de l\'ajustement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Update GDPR consent
  const handleUpdateGDPR = async (clientId, smsConsent, emailConsent) => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${clientId}/gdpr`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sms_marketing: smsConsent,
          email_marketing: emailConsent
        })
      });
      if (res.ok) {
        toast.success('Consentement RGPD mis à jour');
        await loadClients();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // ===== BLOC A - Fonctions Gestion Clients =====
  
  // Créer un nouveau client
  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newClient)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowCreateClientModal(false);
        setNewClient({
          name: '',
          phone: '',
          email: '',
          notes: '',
          loyalty_tier: 'standard',
          sms_marketing: false,
          email_marketing: false
        });
        await loadClients();
        await loadDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Éditer un client
  const handleEditClient = async () => {
    if (!editClient.reason?.trim()) {
      toast.error('La raison de la modification est obligatoire');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${selectedClient.id}/update`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editClient)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowEditClientModal(false);
        setEditClient({
          name: '',
          phone: '',
          email: '',
          notes: '',
          loyalty_tier: 'standard',
          sms_marketing: false,
          email_marketing: false,
          reason: ''
        });
        await loadClients();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la modification');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Ouvrir le modal d'édition avec les données du client
  const openEditClientModal = (client) => {
    setSelectedClient(client);
    setEditClient({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
      loyalty_tier: client.loyalty_tier || 'standard',
      sms_marketing: client.gdpr_consent?.sms_marketing || false,
      email_marketing: client.gdpr_consent?.email_marketing || false,
      reason: ''
    });
    setShowEditClientModal(true);
  };
  
  // Enregistrer une action manuelle
  const handleManualAction = async () => {
    if (!manualAction.reason?.trim()) {
      toast.error('La raison est obligatoire');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${selectedClient.id}/manual-action`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          client_id: selectedClient.id,
          ...manualAction
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        let message = 'Action enregistrée';
        if (data.points_earned > 0) {
          message += ` (+${data.points_earned} pts)`;
        }
        if (data.visit_added) {
          message += ` (+1 visite)`;
        }
        toast.success(message);
        setShowManualActionModal(false);
        setManualAction({
          action_type: 'purchase',
          amount: 0,
          category: 'autre',
          description: '',
          reason: '',
          add_visit: true,
          calculate_points: true
        });
        await loadClients();
        await loadDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Charger l'historique des modifications d'un client
  const loadClientModificationHistory = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${clientId}/modification-history`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setClientModificationHistory(data.history || []);
        setShowClientHistoryModal(true);
      }
    } catch (error) {
      toast.error('Erreur chargement historique');
    }
  };
  
  // Changer le statut d'un client
  const handleChangeClientStatus = async (clientId, newTier, reason) => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${clientId}/status?loyalty_tier=${newTier}&reason=${encodeURIComponent(reason)}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await loadClients();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Modifier le nombre de visites
  const handleUpdateVisitCount = async (clientId, newCount, reason) => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${clientId}/visit-count?new_count=${newCount}&reason=${encodeURIComponent(reason)}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await loadClients();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Filtrer les clients pour la recherche rapide dans le modal promo
  useEffect(() => {
    if (promoClientSearch.length >= 2) {
      const filtered = clients.filter(c => 
        c.name?.toLowerCase().includes(promoClientSearch.toLowerCase()) ||
        c.phone?.includes(promoClientSearch) ||
        c.email?.toLowerCase().includes(promoClientSearch.toLowerCase())
      );
      setFilteredClientsForPromo(filtered.slice(0, 10));
    } else {
      setFilteredClientsForPromo([]);
    }
  }, [promoClientSearch, clients]);

  // ===== BLOC B - Fonctions Promotions de Masse =====
  
  // Filtrer les clients selon le statut pour promo de masse
  const getMassPromoFilteredClients = () => {
    if (massPromoFilter === 'all') return clients;
    return clients.filter(c => c.loyalty_tier === massPromoFilter);
  };
  
  // Toggle sélection d'un client pour promo de masse
  const toggleMassPromoClient = (clientId) => {
    setMassPromoClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      }
      return [...prev, clientId];
    });
  };
  
  // Sélectionner/Désélectionner tous les clients filtrés
  const toggleMassPromoSelectAll = () => {
    const filteredIds = getMassPromoFilteredClients().map(c => c.id);
    if (massPromoSelectAll) {
      setMassPromoClients([]);
    } else {
      setMassPromoClients(filteredIds);
    }
    setMassPromoSelectAll(!massPromoSelectAll);
  };
  
  // Créer des promotions de masse
  const handleCreateMassPromo = async () => {
    if (massPromoClients.length === 0) {
      toast.error('Veuillez sélectionner au moins un client');
      return;
    }
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const clientId of massPromoClients) {
        const res = await fetch(`${API_URL}/api/loyalty/promos`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            client_id: clientId,
            promo_type: massPromo.promo_type,
            value: massPromo.value,
            description: massPromo.description,
            validity_days: massPromo.validity_days,
            max_uses: 1,
            send_sms: massPromo.send_sms,
            send_email: massPromo.send_email
          })
        });
        
        if (res.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} promotion(s) créée(s) avec succès`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} erreur(s) lors de la création`);
      }
      
      setShowMassPromoModal(false);
      setMassPromoClients([]);
      setMassPromoSelectAll(false);
      setMassPromo({
        promo_type: 'percent',
        value: 10,
        description: '',
        validity_days: 30,
        send_sms: true,
        send_email: true
      });
      await loadPromos();
      await loadDashboard();
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // ===== BLOC C - Génération IA du texte de promo =====
  
  const generateAIPromoText = async () => {
    setAiPromoGenerating(true);
    setAiPromoResult(null);
    
    try {
      // Si aucun mot-clé, générer automatiquement basé sur la config
      const promoTypeLabels = {
        'percent': 'Remise en pourcentage',
        'fixed': 'Remise fixe en euros',
        'points_conversion': 'Conversion de points fidélité',
        'gift': 'Carte cadeau',
        'free_repair': 'Réparation gratuite',
        'free_purchase': 'Achat gratuit'
      };
      
      const autoKeywords = aiPromoKeywords.trim() || 
        `Promotion ${promoTypeLabels[newPromo.promo_type] || newPromo.promo_type} de ${newPromo.value}${newPromo.promo_type === 'percent' ? '%' : '€'} pour ${newPromo.scope === 'general' ? 'tous les clients' : 'un client fidèle'}`;
      
      const res = await fetch(`${API_URL}/api/loyalty/promos/generate-text`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          keywords: autoKeywords,
          promo_type: newPromo.promo_type,
          value: newPromo.value,
          scope: newPromo.scope,
          validity_days: newPromo.validity_days,
          max_uses: newPromo.max_uses
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiPromoResult(data.generated_text);
        setAiPromoApproved(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setAiPromoGenerating(false);
  };
  
  // Approuver le texte généré par l'IA
  const approveAIPromoText = () => {
    if (aiPromoResult) {
      setNewPromo(prev => ({ ...prev, description: aiPromoResult }));
      setAiPromoApproved(true);
      toast.success('Texte approuvé et appliqué');
      setShowAIPromoModal(false);
    }
  };

  // ===== GÉNÉRATION IA MESSAGE CAMPAGNE =====
  const generateCampaignAIMessage = async () => {
    setCampaignAIGenerating(true);
    setCampaignAIResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/campaign/generate-message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          keywords: campaign.message || 'fidélité, offre spéciale, remerciement',
          promo_type: campaign.promo_type,
          value: campaign.value,
          scope: campaign.tier === 'all' ? 'Tous les clients' : campaign.tier
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCampaignAIResult(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setCampaignAIGenerating(false);
  };
  
  const applyCampaignAIMessage = (version = 'sms') => {
    if (campaignAIResult) {
      const text = version === 'sms' ? campaignAIResult.sms_version : campaignAIResult.email_version;
      setCampaign(prev => ({ ...prev, message: text }));
      toast.success('Message appliqué');
    }
  };

  // ===== SYNCHRONISATION COMPLÈTE AVEC POINTS RÉTROACTIFS =====
  const handleSyncFull = async () => {
    setSyncingFull(true);
    setSyncFullResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/sync-full`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSyncFullResult(data);
        toast.success(data.message);
        
        // Recharger les données
        await Promise.all([loadDashboard(), loadClients()]);
        
        // Charger les validations en attente
        if (data.pending_validations > 0) {
          await loadPendingValidations();
          setShowPendingValidationsModal(true);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setSyncingFull(false);
  };

  // ===== NETTOYAGE DES DOUBLONS DE POINTS =====
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  const [duplicatesAnalysis, setDuplicatesAnalysis] = useState(null);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  const handleAnalyzeDuplicates = async () => {
    setCleaningDuplicates(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/duplicates/analyze`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setDuplicatesAnalysis(data);
        setShowDuplicatesModal(true);
        if (data.duplicate_groups === 0) {
          toast.success('Aucun doublon détecté !');
        }
      }
    } catch (error) {
      toast.error('Erreur analyse doublons');
    }
    setCleaningDuplicates(false);
  };

  const handleCleanDuplicates = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir nettoyer les doublons ? Cette action est irréversible.')) {
      return;
    }
    
    setCleaningDuplicates(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/duplicates/clean`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowDuplicatesModal(false);
        setDuplicatesAnalysis(null);
        
        // Recharger les données
        await Promise.all([loadDashboard(), loadClients()]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur nettoyage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setCleaningDuplicates(false);
  };
  
  const loadPendingValidations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/pending-validations`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingValidations(data.pending);
      }
    } catch (error) {
      console.error('Erreur chargement validations:', error);
    }
  };
  
  const handleApproveValidation = async (validationId) => {
    setProcessingValidation(validationId);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/pending-validations/${validationId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setPendingValidations(prev => prev.filter(p => p.id !== validationId));
        await loadClients();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setProcessingValidation(null);
  };
  
  const handleRejectValidation = async (validationId) => {
    setProcessingValidation(validationId);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/pending-validations/${validationId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Transaction rejetée (doublon confirmé)');
        setPendingValidations(prev => prev.filter(p => p.id !== validationId));
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setProcessingValidation(null);
  };
  
  const handleApproveAllValidations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/pending-validations/approve-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.approved} transactions approuvées (${data.total_points} pts)`);
        setPendingValidations([]);
        await loadClients();
        setShowPendingValidationsModal(false);
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // ===== FONCTIONS NOTIFICATIONS =====
  
  // Charger les paramètres de notification
  const loadNotificationSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/settings`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationSettings(data);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres notifications:', error);
    }
  };
  
  // Charger les clients à notifier
  const loadClientsToNotify = async () => {
    setLoadingNotifications(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/clients-to-notify`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setClientsToNotify(data);
      }
    } catch (error) {
      console.error('Erreur chargement clients à notifier:', error);
    }
    setLoadingNotifications(false);
  };
  
  // Charger l'historique des notifications
  const loadNotificationHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/history?limit=30`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationHistory(data.notifications || []);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };
  
  // Sauvegarder les paramètres de notification
  const saveNotificationSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(notificationSettings)
      });
      if (res.ok) {
        toast.success('Paramètres de notification sauvegardés');
      }
    } catch (error) {
      toast.error('Erreur de sauvegarde');
    }
  };
  
  // Envoyer une incitation individuelle
  const sendIncentiveNotification = async (clientId, notificationType) => {
    setSendingNotification(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/send-incentive/${clientId}?notification_type=${notificationType}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        await loadClientsToNotify();
        await loadNotificationHistory();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
    setSendingNotification(false);
  };
  
  // Envoyer des incitations en masse
  const sendBulkIncentive = async (notificationType) => {
    setSendingNotification(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/send-bulk-incentive?notification_type=${notificationType}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      toast.success(data.message);
      await loadClientsToNotify();
      await loadNotificationHistory();
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
    setSendingNotification(false);
  };
  
  // Envoyer le récap mensuel
  const sendMonthlyRecap = async () => {
    setSendingNotification(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/send-monthly-recap`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      toast.success(data.message);
      await loadNotificationHistory();
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
    setSendingNotification(false);
    setShowConfirmSendModal(false);
  };
  
  // Envoyer une relance manuelle
  const sendRelance = async () => {
    if (!relanceClient) return;
    
    setSendingNotification(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/notifications/send-relance?client_id=${relanceClient.id}${relanceMessage ? `&custom_message=${encodeURIComponent(relanceMessage)}` : ''}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setShowRelanceModal(false);
        setRelanceClient(null);
        setRelanceMessage('');
        await loadNotificationHistory();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
    setSendingNotification(false);
  };

  // Ouvrir modal de confirmation avant envoi bulk
  const openConfirmSendModal = (type) => {
    let clientsList = [];
    if (type === 'near_threshold') {
      clientsList = clientsToNotify.near_threshold || [];
    } else if (type === 'ready_to_redeem') {
      clientsList = clientsToNotify.ready_to_redeem || [];
    } else if (type === 'monthly_recap') {
      // Pour le recap mensuel, on prend tous les clients avec points
      clientsList = clients.filter(c => c.points_balance > 0);
    }
    
    // Filtrer ceux qui ont le consentement RGPD
    const eligibleClients = clientsList.filter(c => 
      (c.gdpr_email || c.email_marketing || c.gdpr_consent?.email_marketing) ||
      (c.gdpr_sms || c.sms_marketing || c.gdpr_consent?.sms_marketing)
    );
    
    setConfirmSendType(type);
    setConfirmSendClients(eligibleClients);
    setShowConfirmSendModal(true);
  };

  // Confirmer et envoyer les notifications
  const confirmAndSendNotifications = async () => {
    if (confirmSendType === 'monthly_recap') {
      await sendMonthlyRecap();
    } else {
      await sendBulkIncentive(confirmSendType);
    }
    setShowConfirmSendModal(false);
  };

  // Calculer la conversion points → euros pour affichage
  const calculatePointsValue = (points) => {
    const tierFixedPoints = settings?.tier_fixed_points || 100;
    const tierFixedValue = settings?.tier_fixed_value || 5;
    
    if (points >= tierFixedPoints) {
      const tiers = Math.floor(points / tierFixedPoints);
      return tiers * tierFixedValue;
    }
    return 0;
  };

  // Ouvrir modal de consommation de points
  const openConsumePointsModal = (client) => {
    setConsumePointsClient(client);
    setConsumePointsData({
      points_to_use: Math.min(client.points_balance || 0, settings?.tier_fixed_points || 100),
      redemption_type: 'fixed',
      sale_amount: 0,
      category: 'autre'
    });
    setShowConsumePointsModal(true);
  };

  // Consommer les points d'un client
  const handleConsumePoints = async () => {
    if (!consumePointsClient || consumePointsData.sale_amount <= 0) {
      toast.error('Veuillez entrer un montant de vente valide');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/points/redeem`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          client_id: consumePointsClient.id,
          points_to_use: consumePointsData.points_to_use,
          redemption_type: consumePointsData.redemption_type,
          sale_amount: consumePointsData.sale_amount,
          category: consumePointsData.category
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Remise de ${data.discount_applied?.toFixed(2) || 0}€ appliquée ! ${data.points_used || 0} points utilisés.`);
        setShowConsumePointsModal(false);
        await loadClients();
        await loadDashboard();
      } else {
        toast.error(data.detail || 'Erreur lors de la consommation des points');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Charger les données de notification au montage
  useEffect(() => {
    loadNotificationSettings();
    loadClientsToNotify();
    loadNotificationHistory();
  }, []);

  // Load report on tab change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadPointsReport();
    } else if (activeTab === 'consumption') {
      loadConsumptionDashboard();
      loadAvoirs();
    } else if (activeTab === 'reporting') {
      loadReportingDashboard();
    }
  }, [activeTab, loadPointsReport, loadConsumptionDashboard, loadAvoirs, consumptionPeriod, loadReportingDashboard, reportingPeriod]);

  // Save settings
  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Paramètres sauvegardés');
        setSettingsChanged(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // ===== AUDIT & NETTOYAGE FONCTIONS =====
  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/audit/clients`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
        toast.success('Audit terminé');
      } else {
        toast.error('Erreur lors de l\'audit');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setAuditLoading(false);
    }
  };

  const runCleanupDryRun = async () => {
    setCleanupLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/duplicates?dry_run=true&keep_strategy=most_points`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCleanupResult(data);
        toast.success('Prévisualisation terminée');
      } else {
        toast.error('Erreur lors de la prévisualisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setCleanupLoading(false);
    }
  };

  const runCleanupForReal = async () => {
    if (!window.confirm('⚠️ ATTENTION: Cette action va supprimer les clients en doublon. Êtes-vous sûr ?')) {
      return;
    }
    setCleanupLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/duplicates?dry_run=false&keep_strategy=most_points`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCleanupResult(data);
        toast.success(`${data.clients_to_delete} clients supprimés !`);
        loadDashboard();
        loadClients();
      } else {
        toast.error('Erreur lors du nettoyage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setCleanupLoading(false);
    }
  };

  const runResetAll = () => {
    // Ouvre le modal OTP au lieu d'exécuter directement
    setShowResetOtpModal(true);
    setResetOtpSent(false);
    setResetOtpCode('');
  };

  // Demander l'envoi du code OTP
  const requestResetOtp = async () => {
    setResetOtpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/request-reset-otp?email=${encodeURIComponent(resetOtpEmail)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setResetOtpSent(true);
        toast.success(`Code envoyé à ${data.email_sent_to}`);
      } else {
        toast.error(data.detail || 'Erreur envoi code');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setResetOtpLoading(false);
    }
  };

  // Exécuter le reset avec le code OTP
  const executeResetWithOtp = async () => {
    if (!resetOtpCode || resetOtpCode.length !== 6) {
      toast.error('Entrez le code à 6 chiffres');
      return;
    }
    setResetOtpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/reset-all-clients?otp_code=${resetOtpCode}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.clients_deleted} clients supprimés. Backup: ${data.backup_id || 'aucun'}`);
        setShowResetOtpModal(false);
        setAuditData(null);
        setCleanupResult(null);
        loadDashboard();
        loadClients();
        loadBackups(); // Recharger les backups
      } else {
        toast.error(data.detail || 'Erreur lors du reset');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setResetOtpLoading(false);
    }
  };

  // ===== RECALCUL DES POINTS =====
  const runRecalculatePoints = async (dryRun = true) => {
    setRecalculateLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/recalculate-points?dry_run=${dryRun}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setRecalculateResult(data);
        if (dryRun) {
          toast.info(`Prévisualisation: ${data.clients_with_changes} clients à corriger`);
        } else {
          toast.success(`${data.clients_with_changes} clients corrigés ! Économie: ${data.debt_saved_eur}€`);
          loadDashboard();
          runAudit(); // Rafraîchir l'audit
        }
      } else {
        toast.error(data.detail || 'Erreur lors du recalcul');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setRecalculateLoading(false);
    }
  };

  // ===== BACKUPS =====
  const loadBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/backups`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setBackups(data.backups || []);
      }
    } catch (error) {
      console.error('Erreur chargement backups:', error);
    } finally {
      setBackupsLoading(false);
    }
  };

  const restoreBackup = async (backupId) => {
    if (!window.confirm('Restaurer ce backup ? La base actuelle doit être vide.')) return;
    setBackupsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/restore/${backupId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Restauration: ${data.clients_restored} clients, ${data.transactions_restored} transactions`);
        setShowBackupsModal(false);
        loadDashboard();
        loadClients();
        runAudit();
      } else {
        toast.error(data.detail || 'Erreur restauration');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setBackupsLoading(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Supprimer définitivement ce backup ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/backups/${backupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Backup supprimé');
        loadBackups();
      } else {
        toast.error('Erreur suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // ===== RATTRAPAGE DES POINTS =====
  const initLoyaltyFields = async () => {
    if (!window.confirm('Initialiser les champs de fidélité à 0 pour TOUS les clients ? Cette action remet tous les compteurs à zéro avant le rattrapage.')) return;
    setInitLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/init-loyalty-fields`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.clients_updated} clients initialisés`);
        loadDashboard();
        loadClients(false);
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setInitLoading(false);
    }
  };

  const runRattrapagePoints = async (dryRun = true) => {
    setRattrapageLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/cleanup/rattrapage-points?dry_run=${dryRun}&points_per_euro=1`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setRattrapageResult(data);
        if (dryRun) {
          toast.info(`Prévisualisation: ${data.clients_updated} clients à mettre à jour`);
        } else {
          toast.success(`Rattrapage effectué: ${data.clients_updated} clients mis à jour !`);
          loadDashboard();
          loadClients(false);
        }
      } else {
        toast.error(data.detail || 'Erreur lors du rattrapage');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setRattrapageLoading(false);
    }
  };

  // Create promo
  const handleCreatePromo = async () => {
    if (!newPromo.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/promos`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newPromo)
      });
      if (res.ok) {
        toast.success('Promotion créée avec succès');
        setShowNewPromoModal(false);
        setNewPromo({
          client_id: '',
          promo_type: 'percent',
          value: 10,
          description: '',
          validity_days: 30,
          max_uses: 1,
          send_sms: true,
          send_email: true
        });
        await loadPromos();
        await loadDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Send campaign
  const handleSendCampaign = async () => {
    try {
      const params = new URLSearchParams();
      
      // Si sélection manuelle de clients
      if (campaign.tier === 'selected' && campaign.selectedClients.length > 0) {
        params.append('tier', 'selected');
        // Envoyer les IDs des clients sélectionnés dans le body
        const res = await fetch(`${API_URL}/api/loyalty/send-campaign?${params}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            promo_type: campaign.promo_type,
            value: campaign.value,
            message: campaign.message || null,
            send_sms: campaign.send_sms,
            send_email: campaign.send_email,
            client_ids: campaign.selectedClients.map(c => c.id)
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          toast.success(data.message);
          setShowCampaignModal(false);
          setCampaign(prev => ({ ...prev, selectedClients: [] }));
          await loadPromos();
          await loadDashboard();
        } else {
          toast.error('Erreur lors de l\'envoi');
        }
      } else {
        // Envoi par tier
        params.append('tier', campaign.tier);
        params.append('promo_type', campaign.promo_type);
        params.append('value', campaign.value);
        if (campaign.message) params.append('message', campaign.message);
        params.append('send_sms', campaign.send_sms);
        params.append('send_email', campaign.send_email);
        
        const res = await fetch(`${API_URL}/api/loyalty/send-campaign?${params}`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        
        if (res.ok) {
          const data = await res.json();
          toast.success(data.message);
          setShowCampaignModal(false);
          await loadPromos();
          await loadDashboard();
        } else {
          toast.error('Erreur lors de l\'envoi');
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Load client history
  const handleViewClient = async (client) => {
    setSelectedClient(client);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/clients/${client.id}/history`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setClientHistory(await res.json());
        setShowClientModal(true);
      }
    } catch (error) {
      toast.error('Erreur chargement historique');
    }
  };

  // View promo card
  const handleViewPromoCard = async (promo, format = 'digital') => {
    try {
      const res = await fetch(`${API_URL}/api/loyalty/promos/${promo.id}/card?format=${format}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedPromo(promo);
        setPromoCardHtml(data.html);
        setShowPromoCardModal(true);
      }
    } catch (error) {
      toast.error('Erreur génération carte');
    }
  };

  // Cancel promo
  const handleCancelPromo = async (promoId) => {
    if (!window.confirm('Annuler cette promotion ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/loyalty/promos/${promoId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Promotion annulée');
        await loadPromos();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Resend promo
  const handleResendPromo = async (promo) => {
    try {
      const params = new URLSearchParams();
      params.append('send_sms', 'true');
      params.append('send_email', 'true');
      
      const res = await fetch(`${API_URL}/api/loyalty/promos/${promo.id}/send?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Promo renvoyée (SMS: ${data.sms_sent ? 'Oui' : 'Non'}, Email: ${data.email_sent ? 'Oui' : 'Non'})`);
      }
    } catch (error) {
      toast.error('Erreur d\'envoi');
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      standard: 'bg-gray-100 text-gray-700',
      bronze: 'bg-amber-100 text-amber-700',
      silver: 'bg-slate-200 text-slate-700',
      gold: 'bg-yellow-100 text-yellow-700'
    };
    const icons = {
      standard: <Users className="w-3 h-3" />,
      bronze: <Award className="w-3 h-3" />,
      silver: <Star className="w-3 h-3" />,
      gold: <Crown className="w-3 h-3" />
    };
    return (
      <Badge className={`${styles[tier] || styles.standard} gap-1`}>
        {icons[tier] || icons.standard}
        {tier?.charAt(0).toUpperCase() + tier?.slice(1) || 'Standard'}
      </Badge>
    );
  };

  const getPromoStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      used: 'bg-blue-100 text-blue-700',
      expired: 'bg-gray-100 text-gray-500',
      cancelled: 'bg-red-100 text-red-700'
    };
    const labels = {
      active: 'Active',
      used: 'Utilisée',
      expired: 'Expirée',
      cancelled: 'Annulée'
    };
    return <Badge className={styles[status] || styles.active}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Gift className="h-7 w-7" />
                  Programme Fidélité
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/80 hover:text-white hover:bg-white/20 ml-1"
                    onClick={() => setShowInfoModal(true)}
                    title="Comment ça marche ?"
                    data-testid="loyalty-info-btn"
                  >
                    <HelpCircle className="h-5 w-5" />
                  </Button>
                </h1>
                <p className="text-white/80 text-sm">Gérez vos clients fidèles et promotions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSyncClients}
                disabled={syncing}
                className="gap-2"
                title="Synchronisation rapide des clients"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sync...' : 'Sync Rapide'}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSyncFull}
                disabled={syncingFull}
                className="gap-2 bg-amber-500 hover:bg-amber-600"
                title="Import complet des clients (SANS attribution automatique de points)"
              >
                <Zap className={`h-4 w-4 ${syncingFull ? 'animate-pulse' : ''}`} />
                {syncingFull ? 'Import...' : 'Import Clients'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAnalyzeDuplicates}
                disabled={cleaningDuplicates}
                className="gap-2 border-red-400 text-red-400 hover:bg-red-500/20"
                title="Analyser et nettoyer les doublons de points"
              >
                <Trash2 className={`h-4 w-4 ${cleaningDuplicates ? 'animate-pulse' : ''}`} />
                {cleaningDuplicates ? 'Analyse...' : 'Nettoyer doublons'}
              </Button>
              {pendingValidations.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowPendingValidationsModal(true)}
                  className="gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {pendingValidations.length} Doublons
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleUpdateLoyaltyStatus}
                disabled={updatingStatus}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${updatingStatus ? 'animate-pulse' : ''}`} />
                Mettre à jour
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Tableau de bord</span>
              <span className="sm:hidden">Accueil</span>
            </TabsTrigger>
            <TabsTrigger value="clients" className="gap-2" data-testid="tab-clients">
              <Users className="h-4 w-4" />
              Clients
              {dashboard?.clients?.loyal > 0 && (
                <Badge variant="secondary" className="ml-1">{dashboard.clients.loyal}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="promos" className="gap-2" data-testid="tab-promos">
              <Ticket className="h-4 w-4" />
              <span className="hidden sm:inline">Promotions</span>
              <span className="sm:hidden">Promos</span>
              {dashboard?.promos?.active > 0 && (
                <Badge variant="secondary" className="ml-1">{dashboard.promos.active}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="consumption" className="gap-2" data-testid="tab-consumption">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Consommation</span>
              <span className="sm:hidden">Conso</span>
            </TabsTrigger>
            <TabsTrigger value="reporting" className="gap-2" data-testid="tab-reporting">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Reporting</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2" data-testid="tab-notifications">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notifications</span>
              <span className="sm:hidden">Notifs</span>
              {(clientsToNotify.near_threshold_count > 0 || clientsToNotify.ready_to_redeem_count > 0) && (
                <Badge variant="destructive" className="ml-1">
                  {(clientsToNotify.near_threshold_count || 0) + (clientsToNotify.ready_to_redeem_count || 0)}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Paramètres</span>
              <span className="sm:hidden">Config</span>
            </TabsTrigger>
            <TabsTrigger value="admin" className="gap-2 text-red-600" data-testid="tab-admin">
              <Wrench className="h-4 w-4" />
              <span className="hidden sm:inline">Admin</span>
              <span className="sm:hidden">Admin</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  setClientsFilter('all');
                  setActiveTab('clients');
                }}
                data-testid="total-clients-card"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Clients</p>
                      <p className="text-3xl font-bold">{dashboard?.clients?.total || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  setClientsFilter('loyal');
                  setActiveTab('clients');
                }}
                data-testid="loyal-clients-card"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Clients Fidèles</p>
                      <p className="text-3xl font-bold text-violet-600">{dashboard?.clients?.loyal || 0}</p>
                    </div>
                    <div className="p-3 bg-violet-100 rounded-full">
                      <Crown className="h-6 w-6 text-violet-600" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Badge 
                      className="bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientsFilter('bronze');
                        setActiveTab('clients');
                      }}
                      data-testid="bronze-badge"
                    >
                      {dashboard?.clients?.by_tier?.bronze || 0} Bronze
                    </Badge>
                    <Badge 
                      className="bg-slate-200 text-slate-700 cursor-pointer hover:bg-slate-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientsFilter('silver');
                        setActiveTab('clients');
                      }}
                      data-testid="silver-badge"
                    >
                      {dashboard?.clients?.by_tier?.silver || 0} Silver
                    </Badge>
                    <Badge 
                      className="bg-yellow-100 text-yellow-700 cursor-pointer hover:bg-yellow-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientsFilter('gold');
                        setActiveTab('clients');
                      }}
                      data-testid="gold-badge"
                    >
                      {dashboard?.clients?.by_tier?.gold || 0} Gold
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Points Distribués</p>
                      <p className="text-3xl font-bold text-amber-600">{pointsReport?.points_earned?.total || 0}</p>
                    </div>
                    <div className="p-3 bg-amber-100 rounded-full">
                      <Coins className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    = {((pointsReport?.points_earned?.total || 0) / 20).toFixed(0)}€ de remise potentielle
                  </p>
                  <p className="text-xs text-muted-foreground">
                    sur {pointsReport?.points_earned?.from_amount?.toFixed(0) || 0}€ d'achats ce mois
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Points Utilisés</p>
                      <p className="text-3xl font-bold text-green-600">{pointsReport?.points_used?.total || 0}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <Gift className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    = {pointsReport?.points_used?.discount_value?.toFixed(2) || 0}€ de remises
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Points par catégorie */}
            {pointsReport?.by_category?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Points par Catégorie (ce mois)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {pointsReport.by_category.map((cat) => (
                      <div key={cat.category} className="p-3 bg-slate-50 rounded-lg text-center">
                        <Badge className={PRODUCT_CATEGORIES[cat.category]?.color || 'bg-slate-100'}>
                          {cat.label}
                        </Badge>
                        <p className="text-xl font-bold mt-2">{cat.points}</p>
                        <p className="text-xs text-muted-foreground">{cat.amount}€</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Info catégories */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Règles des Catégories</h3>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700">Réparation</Badge>
                        <span className="text-amber-700">Max 10€ ou 10%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Accessoire</Badge>
                        <span className="text-amber-700">Jusqu'à 50%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-gray-100 text-gray-500">Dématérialisé</Badge>
                        <span className="text-amber-700">Exclu (0 pts)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-2 border-dashed border-violet-200 hover:border-violet-400 cursor-pointer transition-colors"
                onClick={() => setShowNewPromoModal(true)}>
                <CardContent className="pt-6 flex items-center gap-4">
                  <div className="p-4 bg-violet-100 rounded-xl">
                    <UserPlus className="h-8 w-8 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Créer une Promotion</h3>
                    <p className="text-muted-foreground text-sm">Envoyer une offre personnalisée à un client</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
                </CardContent>
              </Card>

              <Card className="border-2 border-dashed border-purple-200 hover:border-purple-400 cursor-pointer transition-colors"
                onClick={() => setShowCampaignModal(true)}>
                <CardContent className="pt-6 flex items-center gap-4">
                  <div className="p-4 bg-purple-100 rounded-xl">
                    <Send className="h-8 w-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Campagne SMS/Email</h3>
                    <p className="text-muted-foreground text-sm">Envoyer une offre à tous les clients fidèles</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
                </CardContent>
              </Card>
            </div>

            {/* Top Clients & Recent Promos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-yellow-500" />
                    Top Clients (Points)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.top_clients?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.top_clients.map((client, idx) => (
                        <div key={client.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{client.name}</p>
                              <p className="text-sm text-muted-foreground">
                                <Coins className="inline h-3 w-3 mr-1" />
                                {client.points_balance || 0} pts ({client.visit_count || 0} visites)
                              </p>
                            </div>
                          </div>
                          {getTierBadge(client.loyalty_tier)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Aucun client fidèle pour le moment
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-green-500" />
                    Promotions Récentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_promos?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_promos.map((promo) => (
                        <div key={promo.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{promo.client_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {promo.code} - {promo.value}{promo.promo_type === 'percent' ? '%' : '€'}
                            </p>
                          </div>
                          {getPromoStatusBadge(promo.status)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Aucune promotion créée
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-2">
                <Select value={clientsFilter} onValueChange={setClientsFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filtrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="loyal">Fidèles</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="bronze">Bronze</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={clientsSearch}
                    onChange={(e) => setClientsSearch(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-base" data-testid="clients-count-badge">
                  {clients.length} affichés / {clientsTotal} total
                </Badge>
                <Button onClick={() => setShowCreateClientModal(true)} className="gap-2" data-testid="new-client-btn">
                  <UserPlus className="h-4 w-4" />
                  Nouveau Client
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left p-4 font-medium bg-slate-50">Client</th>
                        <th className="text-left p-4 font-medium bg-slate-50">Contact</th>
                        <th className="text-center p-4 font-medium bg-slate-50">Points</th>
                        <th className="text-center p-4 font-medium bg-slate-50">Statut</th>
                        <th className="text-center p-4 font-medium bg-slate-50">RGPD</th>
                        <th className="text-right p-4 font-medium bg-slate-50">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {clients.map((client) => (
                        <tr key={client.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <p 
                              className="font-medium cursor-pointer text-violet-600 hover:text-violet-800 hover:underline"
                              onClick={() => openEditClientModal(client)}
                              data-testid={`client-name-${client.id}`}
                            >
                              {client.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {client.visit_count || 0} visite(s)
                            </p>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              {client.phone && (
                                <span className="flex items-center gap-1 text-sm">
                                  <Phone className="h-3 w-3" /> {client.phone}
                                </span>
                              )}
                              {client.email && (
                                <span className="flex items-center gap-1 text-sm">
                                  <Mail className="h-3 w-3" /> {client.email}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center">
                              <Badge className="bg-amber-100 text-amber-700 text-lg font-bold gap-1">
                                <Coins className="h-4 w-4" />
                                {client.points_balance || 0}
                              </Badge>
                              {(client.points_balance || 0) >= (settings?.tier_fixed_points || 100) && (
                                <span className="text-xs text-green-600 font-medium mt-1">
                                  = {calculatePointsValue(client.points_balance || 0)}€ de remise
                                </span>
                              )}
                              {(client.points_balance || 0) > 0 && (client.points_balance || 0) < (settings?.tier_fixed_points || 100) && (
                                <span className="text-xs text-muted-foreground mt-1">
                                  Encore {(settings?.tier_fixed_points || 100) - (client.points_balance || 0)} pts pour {settings?.tier_fixed_value || 5}€
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            {getTierBadge(client.loyalty_tier || 'standard')}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center gap-1">
                              {client.gdpr_consent?.sms_marketing && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <Phone className="h-2 w-2 mr-1" /> SMS OK
                                </Badge>
                              )}
                              {client.gdpr_consent?.email_marketing && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  <Mail className="h-2 w-2 mr-1" /> Email OK
                                </Badge>
                              )}
                              {!client.gdpr_consent?.sms_marketing && !client.gdpr_consent?.email_marketing && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                  <ShieldCheck className="h-2 w-2 mr-1" /> Non défini
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditClientModal(client)}
                                title="Modifier le client"
                                data-testid={`edit-client-${client.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  setSelectedClient(client);
                                  await loadClientPoints(client.id);
                                  setShowPointsModal(true);
                                }}
                                title="Voir les points"
                              >
                                <Coins className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setShowManualActionModal(true);
                                }}
                                title="Action manuelle (achat/réparation)"
                                data-testid={`manual-action-${client.id}`}
                              >
                                <Wrench className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedClient(client);
                                  loadClientModificationHistory(client.id);
                                }}
                                title="Historique modifications"
                                data-testid={`history-${client.id}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setNewPromo(prev => ({ ...prev, client_id: client.id }));
                                  setShowNewPromoModal(true);
                                }}
                                title="Créer promo"
                              >
                                <Gift className="h-4 w-4" />
                              </Button>
                              {(client.points_balance || 0) >= (settings?.tier_fixed_points || 100) && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openConsumePointsModal(client)}
                                  title="Utiliser les points"
                                  data-testid={`consume-points-${client.id}`}
                                >
                                  <Euro className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Bouton charger plus */}
                {clientsHasMore && (
                  <div className="p-4 text-center border-t">
                    <Button 
                      onClick={loadMoreClients} 
                      variant="outline"
                      className="w-full max-w-xs"
                      data-testid="load-more-clients"
                    >
                      Charger plus ({clients.length} / {clientsTotal})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promos Tab */}
          <TabsContent value="promos" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-2">
                <Select value={promosFilter} onValueChange={setPromosFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filtrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="active">Actives</SelectItem>
                    <SelectItem value="used">Utilisées</SelectItem>
                    <SelectItem value="expired">Expirées</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setShowNewPromoModal(true)} className="gap-2">
                  <Gift className="h-4 w-4" />
                  Nouvelle Promo
                </Button>
                <Button 
                  onClick={() => setShowMassPromoModal(true)} 
                  variant="outline"
                  className="gap-2"
                  data-testid="mass-promo-btn"
                >
                  <Users className="h-4 w-4" />
                  Promo Générale
                </Button>
              </div>
              <Badge variant="outline" className="text-base">
                {promos.length} promotions
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promos.map((promo) => (
                <Card key={promo.id} className="overflow-hidden">
                  <div className={`h-2 ${
                    promo.status === 'active' ? 'bg-green-500' :
                    promo.status === 'used' ? 'bg-blue-500' :
                    promo.status === 'expired' ? 'bg-gray-400' :
                    'bg-red-500'
                  }`} />
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-bold text-lg">{promo.code}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-muted-foreground">{promo.client_name || 'Tous les clients'}</p>
                          <Badge className={promo.scope === 'general' ? 'bg-blue-100 text-blue-700 text-xs' : 'bg-violet-100 text-violet-700 text-xs'}>
                            {promo.scope === 'general' ? 'Générale' : 'Individuelle'}
                          </Badge>
                        </div>
                      </div>
                      {getPromoStatusBadge(promo.status)}
                    </div>
                    
                    <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-lg p-3 mb-3 text-center">
                      <p className="text-2xl font-bold text-violet-700">
                        {promo.promo_type === 'percent' ? `-${promo.value}%` : `${promo.value}€`}
                      </p>
                      <p className="text-sm text-violet-600">{promo.type_label}</p>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expire le</span>
                        <span>{new Date(promo.expires_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Utilisations</span>
                        <span>{promo.uses_count || 0} / {promo.max_uses}</span>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewPromoCard(promo)}
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Carte
                      </Button>
                      {promo.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendPromo(promo)}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleCancelPromo(promo.id)}
                            title="Annuler"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeletePromo(promo.id)}
                        title="Supprimer définitivement"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Consumption Tab - Suivi de consommation */}
          <TabsContent value="consumption" className="space-y-6">
            {/* Header avec période */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-violet-600" />
                Suivi de Consommation
              </h2>
              <div className="flex items-center gap-2">
                <Select 
                  value={consumptionPeriod} 
                  onValueChange={(v) => setConsumptionPeriod(v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  onClick={loadConsumptionDashboard}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700">Points Gagnés</p>
                      <p className="text-3xl font-bold text-green-800">
                        {consumptionDashboard?.stats?.points_earned || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <Plus className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-sm text-green-600 mt-2">
                    {consumptionDashboard?.stats?.points_earned_count || 0} transaction(s)
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-700">Points Utilisés</p>
                      <p className="text-3xl font-bold text-amber-800">
                        {consumptionDashboard?.stats?.points_redeemed || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-amber-100 rounded-full">
                      <Minus className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-sm text-amber-600 mt-2">
                    = {consumptionDashboard?.stats?.discount_total || 0}€ de remises
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700">Points Remboursés</p>
                      <p className="text-3xl font-bold text-red-800">
                        {consumptionDashboard?.stats?.points_refunded || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-full">
                      <RotateCcw className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <p className="text-sm text-red-600 mt-2">
                    {consumptionDashboard?.stats?.points_refunded_count || 0} remboursement(s)
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-violet-700">Solde Net</p>
                      <p className="text-3xl font-bold text-violet-800">
                        {consumptionDashboard?.stats?.balance_net || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-violet-100 rounded-full">
                      <Coins className="h-6 w-6 text-violet-600" />
                    </div>
                  </div>
                  <p className="text-sm text-violet-600 mt-2">
                    {consumptionDashboard?.stats?.promos_used || 0} promo(s) utilisée(s)
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Avoirs Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-blue-600" />
                    Avoirs
                  </CardTitle>
                  <CardDescription>
                    Gestion des avoirs clients (remboursements)
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAvoirModal(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Créer un Avoir
                </Button>
              </CardHeader>
              <CardContent>
                {avoirs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucun avoir</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {avoirs.map((avoir) => (
                      <div 
                        key={avoir.id} 
                        className={`p-3 rounded-lg border ${avoir.is_used ? 'bg-slate-50 border-slate-200' : 'bg-blue-50 border-blue-200'}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{avoir.client_name}</p>
                            <p className="text-sm text-muted-foreground">{avoir.reason}</p>
                            <p className="text-xs text-muted-foreground">
                              Expire le {new Date(avoir.expires_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-blue-700">{avoir.remaining_amount}€</p>
                            {avoir.used_amount > 0 && (
                              <p className="text-xs text-muted-foreground">
                                Utilisé: {avoir.used_amount}€
                              </p>
                            )}
                            {avoir.points_deducted > 0 && (
                              <Badge className="bg-red-100 text-red-700 text-xs mt-1">
                                -{avoir.points_deducted} pts déduits
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteAvoir(avoir.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Transactions - DÉTAILLÉ */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-violet-600" />
                  Dernières Transactions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!consumptionDashboard?.recent_transactions?.length ? (
                  <p className="text-center text-muted-foreground py-4">Aucune transaction récente</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {consumptionDashboard.recent_transactions.map((tx, idx) => {
                      // Déterminer le type de source
                      const getSourceInfo = () => {
                        const refType = tx.reference_type || tx.source || tx.transaction_type;
                        switch(refType) {
                          case 'invoice': return { icon: '📄', label: 'Facture' };
                          case 'entry': return { icon: '💵', label: 'Encaissement' };
                          case 'storage': case 'bon_entree': return { icon: '🔧', label: 'Bon d\'entrée' };
                          case 'order': return { icon: '📦', label: 'Commande' };
                          case 'welcome_bonus': return { icon: '🎉', label: 'Bienvenue' };
                          case 'manual_action': case 'manual': return { icon: '👤', label: 'Admin' };
                          case 'redeem': return { icon: '🎁', label: 'Utilisation' };
                          case 'refund': return { icon: '↩️', label: 'Remboursement' };
                          default: return { icon: '📝', label: refType || 'Transaction' };
                        }
                      };
                      const sourceInfo = getSourceInfo();
                      const isDebit = tx.transaction_type === 'refund' || tx.transaction_type === 'redeem';
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border-l-4 ${
                            isDebit 
                              ? 'bg-red-50 border-l-red-500' 
                              : 'bg-green-50 border-l-green-500'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{tx.client_name}</p>
                              <p className="text-sm text-muted-foreground">{tx.client_phone}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {sourceInfo.icon} {sourceInfo.label}
                                </Badge>
                                {tx.amount_ttc > 0 && (
                                  <span className="text-xs text-muted-foreground">{tx.amount_ttc.toFixed(2)}€</span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(tx.created_at).toLocaleDateString('fr-FR')} à {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className={isDebit ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                                {isDebit ? 'Débit' : 'Crédit'}
                              </Badge>
                              <p className="text-lg font-bold mt-1">
                                {isDebit ? '-' : '+'}{Math.abs(tx.points)} pts
                              </p>
                              {tx.discount_value > 0 && (
                                <p className="text-sm text-amber-700">= {tx.discount_value}€</p>
                              )}
                            </div>
                          </div>
                          {tx.reason && (
                            <p className="text-sm text-muted-foreground mt-2 italic">"{tx.reason}"</p>
                          )}
                          {tx.notes && !tx.reason && (
                            <p className="text-xs text-slate-500 mt-1">{tx.notes}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Consumers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-600" />
                  Top Utilisateurs de Points
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!consumptionDashboard?.top_consumers?.length ? (
                  <p className="text-center text-muted-foreground py-4">Aucune consommation</p>
                ) : (
                  <div className="space-y-2">
                    {consumptionDashboard.top_consumers.map((consumer, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium">{consumer.client_name}</p>
                            <p className="text-sm text-muted-foreground">{consumer.count} utilisation(s)</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-700">{consumer.total_points} pts</p>
                          <p className="text-sm text-green-600">= {consumer.total_discount?.toFixed(2) || 0}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reporting Tab - Dashboard de Surveillance */}
          <TabsContent value="reporting" className="space-y-6">
            {/* Header avec période */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-violet-600" />
                Tableau de Bord Rentabilité
              </h2>
              <div className="flex items-center gap-2">
                <Select 
                  value={reportingPeriod} 
                  onValueChange={(v) => setReportingPeriod(v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  onClick={loadReportingDashboard}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Alertes */}
            {reportingDashboard?.alerts?.length > 0 && (
              <div className="space-y-2">
                {reportingDashboard.alerts.map((alert, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg flex items-center gap-2 ${
                      alert.type === 'warning' ? 'bg-amber-50 border border-amber-200 text-amber-800' :
                      'bg-blue-50 border border-blue-200 text-blue-800'
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 3 KPIs principaux */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 1. Burn Rate */}
              <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-600" />
                    Taux d'Utilisation (Burn Rate)
                  </CardTitle>
                  <CardDescription>
                    Points créés vs Points consommés
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className={`text-5xl font-bold ${
                      (reportingDashboard?.burn_rate?.rate_percent || 0) > 80 ? 'text-red-600' :
                      (reportingDashboard?.burn_rate?.rate_percent || 0) > 50 ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {reportingDashboard?.burn_rate?.rate_percent || 0}%
                    </p>
                    <p className={`text-sm mt-1 ${
                      reportingDashboard?.burn_rate?.interpretation === 'Élevé' ? 'text-red-600' :
                      reportingDashboard?.burn_rate?.interpretation === 'Modéré' ? 'text-amber-600' :
                      'text-green-600'
                    }`}>
                      {reportingDashboard?.burn_rate?.interpretation || 'OK'}
                    </p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Points créés</span>
                      <span className="font-medium text-green-600">+{reportingDashboard?.burn_rate?.points_created || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Points utilisés</span>
                      <span className="font-medium text-orange-600">-{reportingDashboard?.burn_rate?.points_burned || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 2. Stock de Points (Dette virtuelle) */}
              <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Coins className="h-5 w-5 text-red-600" />
                    Stock de Points
                  </CardTitle>
                  <CardDescription>
                    "Dette" virtuelle à surveiller
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-5xl font-bold text-red-700">
                      {reportingDashboard?.points_stock?.debt_value_eur || 0}€
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {reportingDashboard?.points_stock?.total_points || 0} pts en circulation
                    </p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg text-center text-sm">
                    <span className="text-red-700">
                      Conversion: {reportingDashboard?.points_stock?.conversion_rate || '100 pts = 5€'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* 3. Panier Moyen Fidélité */}
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5 text-green-600" />
                    Panier Moyen
                  </CardTitle>
                  <CardDescription>
                    Client fidèle vs Client passage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 py-2">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-700">
                        {reportingDashboard?.basket_analysis?.loyalty_avg_basket || 0}€
                      </p>
                      <p className="text-xs text-muted-foreground">Client Fidèle</p>
                      <p className="text-xs text-muted-foreground">
                        ({reportingDashboard?.basket_analysis?.loyalty_sales_count || 0} ventes)
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-500">
                        {reportingDashboard?.basket_analysis?.non_loyalty_avg_basket || 0}€
                      </p>
                      <p className="text-xs text-muted-foreground">Client Passage</p>
                      <p className="text-xs text-muted-foreground">
                        ({reportingDashboard?.basket_analysis?.non_loyalty_sales_count || 0} ventes)
                      </p>
                    </div>
                  </div>
                  <div className={`p-2 rounded-lg text-center text-sm ${
                    (reportingDashboard?.basket_analysis?.uplift_percent || 0) > 0 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {(reportingDashboard?.basket_analysis?.uplift_percent || 0) > 0 ? (
                      <span>✅ Uplift +{reportingDashboard?.basket_analysis?.uplift_percent}% - Programme rentable!</span>
                    ) : (
                      <span>⚠️ Pas d'uplift détecté</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Explication des indicateurs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  Guide des Indicateurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="font-medium text-orange-800">Burn Rate</p>
                    <p className="text-orange-600">
                      &lt;50% = Excellent<br/>
                      50-80% = Modéré<br/>
                      &gt;80% = Attention
                    </p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-800">Stock de Points</p>
                    <p className="text-red-600">
                      C'est votre "dette" virtuelle.<br/>
                      Surveillez si ça augmente trop vite.
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">Panier Moyen</p>
                    <p className="text-green-600">
                      Si client fidèle dépense plus = programme rentable!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            {/* Stats rapides */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-700">Proche du palier</p>
                      <p className="text-3xl font-bold text-amber-800">
                        {clientsToNotify.near_threshold_count || 0}
                      </p>
                    </div>
                    <Target className="h-10 w-10 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700">Prêts à utiliser</p>
                      <p className="text-3xl font-bold text-green-800">
                        {clientsToNotify.ready_to_redeem_count || 0}
                      </p>
                    </div>
                    <Gift className="h-10 w-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700">Clients inactifs</p>
                      <p className="text-3xl font-bold text-red-800">
                        {clientsToNotify.inactive_count || 0}
                      </p>
                    </div>
                    <UserX className="h-10 w-10 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700">Notif. envoyées</p>
                      <p className="text-3xl font-bold text-blue-800">
                        {notificationSettings.total_notifications_sent || 0}
                      </p>
                    </div>
                    <Bell className="h-10 w-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions rapides */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-violet-500" />
                  Actions Rapides
                </CardTitle>
                <CardDescription>
                  Envoyez des notifications en un clic (confirmation requise)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => openConfirmSendModal('near_threshold')}
                    disabled={sendingNotification || clientsToNotify.near_threshold_count === 0}
                    className="gap-2 bg-amber-500 hover:bg-amber-600"
                    data-testid="notify-near-threshold-btn"
                  >
                    <Target className="h-4 w-4" />
                    Notifier "Proche du palier" ({clientsToNotify.near_threshold_count || 0})
                  </Button>
                  
                  <Button
                    onClick={() => openConfirmSendModal('ready_to_redeem')}
                    disabled={sendingNotification || clientsToNotify.ready_to_redeem_count === 0}
                    className="gap-2 bg-green-500 hover:bg-green-600"
                    data-testid="notify-ready-redeem-btn"
                  >
                    <Gift className="h-4 w-4" />
                    Notifier "Remise disponible" ({clientsToNotify.ready_to_redeem_count || 0})
                  </Button>
                  
                  <Button
                    onClick={() => openConfirmSendModal('monthly_recap')}
                    disabled={sendingNotification}
                    variant="outline"
                    className="gap-2"
                    data-testid="monthly-recap-btn"
                  >
                    <Calendar className="h-4 w-4" />
                    Envoyer récap mensuel
                  </Button>
                  
                  <Button
                    onClick={() => {
                      loadClientsToNotify();
                      loadNotificationHistory();
                    }}
                    variant="ghost"
                    className="gap-2"
                    disabled={loadingNotifications}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingNotifications ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Clients proche du palier */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Target className="h-5 w-5" />
                  Clients proches du palier 100 pts
                </CardTitle>
                <CardDescription>
                  Ces clients sont à quelques points d'une récompense
                </CardDescription>
              </CardHeader>
              <CardContent>
                {clientsToNotify.near_threshold?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Aucun client proche du palier</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientsToNotify.near_threshold?.map((client) => (
                      <div key={client.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-amber-700">
                            {client.points_balance} pts • <strong>Plus que {client.points_needed} pts !</strong>
                          </p>
                          <div className="flex gap-2 mt-1">
                            {client.has_email && client.gdpr_email && <Badge className="bg-blue-100 text-blue-700 text-xs">Email OK</Badge>}
                            {client.has_phone && client.gdpr_sms && <Badge className="bg-green-100 text-green-700 text-xs">SMS OK</Badge>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => sendIncentiveNotification(client.id, 'near_threshold')}
                          disabled={sendingNotification}
                          className="gap-1 bg-amber-500 hover:bg-amber-600"
                        >
                          <Send className="h-3 w-3" />
                          Notifier
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Clients prêts à utiliser leurs points */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <Gift className="h-5 w-5" />
                  Clients avec remise disponible
                </CardTitle>
                <CardDescription>
                  Ces clients ont assez de points pour une remise
                </CardDescription>
              </CardHeader>
              <CardContent>
                {clientsToNotify.ready_to_redeem?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Aucun client éligible</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientsToNotify.ready_to_redeem?.map((client) => (
                      <div key={client.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-green-700">
                            <strong>{client.points_balance} pts</strong> disponibles
                          </p>
                          <div className="flex gap-2 mt-1">
                            {client.has_email && client.gdpr_email && <Badge className="bg-blue-100 text-blue-700 text-xs">Email OK</Badge>}
                            {client.has_phone && client.gdpr_sms && <Badge className="bg-green-100 text-green-700 text-xs">SMS OK</Badge>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => sendIncentiveNotification(client.id, 'ready_to_redeem')}
                          disabled={sendingNotification}
                          className="gap-1 bg-green-500 hover:bg-green-600"
                        >
                          <Send className="h-3 w-3" />
                          Notifier
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Clients inactifs (relance manuelle) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <UserX className="h-5 w-5" />
                  Clients inactifs (+30 jours)
                </CardTitle>
                <CardDescription>
                  Relance manuelle uniquement - Cliquez pour personnaliser le message
                </CardDescription>
              </CardHeader>
              <CardContent>
                {clientsToNotify.inactive?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Aucun client inactif</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientsToNotify.inactive?.slice(0, 20).map((client) => (
                      <div key={client.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-red-700">
                            {client.points_balance} pts • Inactif depuis <strong>{client.days_inactive} jours</strong>
                          </p>
                          <div className="flex gap-2 mt-1">
                            {client.has_email && client.gdpr_email && <Badge className="bg-blue-100 text-blue-700 text-xs">Email OK</Badge>}
                            {client.has_phone && client.gdpr_sms && <Badge className="bg-green-100 text-green-700 text-xs">SMS OK</Badge>}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRelanceClient(client);
                            setRelanceMessage('');
                            setShowRelanceModal(true);
                          }}
                          className="gap-1 border-red-300 text-red-700 hover:bg-red-100"
                        >
                          <MessageSquare className="h-3 w-3" />
                          Relancer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Configuration des notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-slate-500" />
                  Configuration des Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Seuil "Proche du palier" (points)</Label>
                    <Input
                      type="number"
                      min="5"
                      max="50"
                      value={notificationSettings.incentive_threshold_near}
                      onChange={(e) => setNotificationSettings(prev => ({ 
                        ...prev, 
                        incentive_threshold_near: parseInt(e.target.value) 
                      }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Notifier si le client est à moins de X pts du palier 100
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Jour d'envoi du récap mensuel</Label>
                    <Select
                      value={String(notificationSettings.monthly_recap_day)}
                      onValueChange={(v) => setNotificationSettings(prev => ({ 
                        ...prev, 
                        monthly_recap_day: parseInt(v) 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1er du mois</SelectItem>
                        <SelectItem value="15">15 du mois</SelectItem>
                        <SelectItem value="28">28 du mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={notificationSettings.prefer_email}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                        ...prev, 
                        prefer_email: checked 
                      }))}
                    />
                    <Label>Email prioritaire (SMS si pas d'email)</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={notificationSettings.smart_incentives_enabled}
                      onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                        ...prev, 
                        smart_incentives_enabled: checked 
                      }))}
                    />
                    <Label>Incitations intelligentes actives</Label>
                  </div>
                </div>

                <Button onClick={saveNotificationSettings} className="gap-2">
                  <Save className="h-4 w-4" />
                  Sauvegarder les paramètres
                </Button>
              </CardContent>
            </Card>

            {/* Historique des notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-indigo-500" />
                  Historique des Envois
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notificationHistory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Aucune notification envoyée</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {notificationHistory.map((notif) => (
                      <div key={notif.id} className="p-3 bg-slate-50 rounded-lg border-l-4 border-l-violet-400">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium">{notif.client_name || 'Client'}</p>
                              <Badge className={`text-xs ${
                                notif.sent_via === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {notif.sent_via === 'email' ? (
                                  <><Mail className="h-3 w-3 mr-1" /> Email</>
                                ) : (
                                  <><Phone className="h-3 w-3 mr-1" /> SMS</>
                                )}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium">
                                {notif.notification_type === 'near_threshold' && '🎯 Proche du palier'}
                                {notif.notification_type === 'ready_to_redeem' && '🎁 Remise disponible'}
                                {notif.notification_type === 'relance' && '📢 Relance'}
                                {notif.notification_type === 'recap' && '📊 Récap mensuel'}
                                {notif.notification_type === 'promo' && '🎟️ Promo envoyée'}
                                {!notif.notification_type && '📨 Notification'}
                              </span>
                            </p>
                            {notif.message && (
                              <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">
                                "{notif.message}"
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-muted-foreground block">
                              {new Date(notif.created_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="text-xs text-muted-foreground block">
                              {new Date(notif.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Système de Points */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-amber-500" />
                    Système de Points
                  </CardTitle>
                  <CardDescription>
                    Configuration de l'accumulation et de l'utilisation des points
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {settings && (
                    <>
                      {/* Nom de la boutique */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="space-y-2">
                          <Label className="text-base font-semibold flex items-center gap-2">
                            <Store className="h-4 w-4 text-blue-600" />
                            Nom de la boutique
                          </Label>
                          <p className="text-sm text-muted-foreground">Affiché dans les SMS et emails</p>
                          <Input
                            value={settings.shop_name || 'PhoneLab'}
                            onChange={(e) => {
                              setSettings(prev => ({ ...prev, shop_name: e.target.value }));
                              setSettingsChanged(true);
                            }}
                            placeholder="PhoneLab"
                            className="max-w-xs"
                          />
                        </div>
                      </div>

                      {/* Points actifs */}
                      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                        <div>
                          <Label className="text-base font-semibold">Système de points activé</Label>
                          <p className="text-sm text-muted-foreground">1€ dépensé = 1 point gagné</p>
                        </div>
                        <Switch
                          checked={settings.points_enabled !== false}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, points_enabled: checked }));
                            setSettingsChanged(true);
                          }}
                        />
                      </div>

                      {/* Bonus Bienvenue */}
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-base font-semibold flex items-center gap-2">
                              <Gift className="h-4 w-4 text-emerald-600" />
                              Bonus Bienvenue
                            </Label>
                            <p className="text-sm text-muted-foreground">Points offerts à l'activation de la fidélité</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 text-center"
                              value={settings.welcome_bonus_points ?? 20}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, welcome_bonus_points: parseInt(e.target.value) || 0 }));
                                setSettingsChanged(true);
                              }}
                            />
                            <span className="text-sm text-muted-foreground">pts</span>
                          </div>
                        </div>
                        <p className="text-xs text-emerald-600 mt-2">
                          Chaque nouveau client recevra automatiquement +{settings.welcome_bonus_points ?? 20} points
                        </p>
                      </div>

                      {/* Paliers de conversion */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-4 bg-green-50 rounded-lg space-y-4">
                          <h4 className="font-semibold flex items-center gap-2 text-green-700">
                            <Euro className="h-4 w-4" />
                            Palier Remise Fixe
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Points requis</Label>
                              <Input
                                type="number"
                                min="1"
                                value={settings.tier_fixed_points || 100}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, tier_fixed_points: parseInt(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Valeur (€)</Label>
                              <Input
                                type="number"
                                min="1"
                                step="0.5"
                                value={settings.tier_fixed_value || 5}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, tier_fixed_value: parseFloat(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                          </div>
                          <p className="text-sm text-green-600">
                            {settings.tier_fixed_points || 100} pts = {settings.tier_fixed_value || 5}€ de remise
                          </p>
                        </div>

                        <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                          <h4 className="font-semibold flex items-center gap-2 text-blue-700">
                            <Percent className="h-4 w-4" />
                            Palier Remise %
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Points requis</Label>
                              <Input
                                type="number"
                                min="1"
                                value={settings.tier_percent_points || 200}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, tier_percent_points: parseInt(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">% de remise</Label>
                              <Input
                                type="number"
                                min="1"
                                max="50"
                                value={settings.tier_percent_value || 10}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, tier_percent_value: parseFloat(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                          </div>
                          <p className="text-sm text-blue-600">
                            {settings.tier_percent_points || 200} pts = {settings.tier_percent_value || 10}% de remise
                          </p>
                        </div>
                      </div>

                      {/* Non-cumul */}
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <Label>Autoriser cumul avec autres promos</Label>
                          <p className="text-xs text-muted-foreground">Par défaut: non cumulable</p>
                        </div>
                        <Switch
                          checked={settings.allow_cumul_with_promos || false}
                          onCheckedChange={(checked) => {
                            setSettings(prev => ({ ...prev, allow_cumul_with_promos: checked }));
                            setSettingsChanged(true);
                          }}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* NOUVEAU: Limites et Expirations */}
              <Card className="border-2 border-amber-200">
                <CardHeader className="bg-amber-50">
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    Limites et Expirations
                  </CardTitle>
                  <CardDescription>
                    Contrôlez la durée de vie et les limites d'utilisation des promos et points
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-4">
                  {settings && (
                    <>
                      {/* Section Promotions */}
                      <div className="p-4 bg-violet-50 rounded-lg space-y-4">
                        <h4 className="font-semibold flex items-center gap-2 text-violet-700">
                          <Ticket className="h-4 w-4" />
                          Promotions
                        </h4>
                        
                        {/* Expiration promos */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Activer l'expiration des promos</Label>
                            <p className="text-xs text-muted-foreground">Si désactivé: promos valides indéfiniment</p>
                          </div>
                          <Switch
                            checked={settings.promo_expiration_enabled || false}
                            onCheckedChange={(checked) => {
                              setSettings(prev => ({ ...prev, promo_expiration_enabled: checked }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                        
                        {settings.promo_expiration_enabled && (
                          <div className="space-y-1 pl-4 border-l-2 border-violet-200">
                            <Label className="text-xs">Durée de validité par défaut (jours)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={settings.default_promo_validity_days || 0}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, default_promo_validity_days: parseInt(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                              className="w-32"
                            />
                            <p className="text-xs text-muted-foreground">0 = pas d'expiration</p>
                          </div>
                        )}
                        
                        {/* Limite d'utilisation promos */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Activer la limite d'utilisation</Label>
                            <p className="text-xs text-muted-foreground">Si désactivé: promos utilisables à volonté</p>
                          </div>
                          <Switch
                            checked={settings.promo_usage_limit_enabled || false}
                            onCheckedChange={(checked) => {
                              setSettings(prev => ({ ...prev, promo_usage_limit_enabled: checked }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                        
                        {settings.promo_usage_limit_enabled && (
                          <div className="space-y-1 pl-4 border-l-2 border-violet-200">
                            <Label className="text-xs">Nombre d'utilisations max par défaut</Label>
                            <Input
                              type="number"
                              min="0"
                              value={settings.default_promo_max_uses || 0}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, default_promo_max_uses: parseInt(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                              className="w-32"
                            />
                            <p className="text-xs text-muted-foreground">0 = illimité</p>
                          </div>
                        )}
                      </div>

                      {/* Section Points */}
                      <div className="p-4 bg-amber-50 rounded-lg space-y-4">
                        <h4 className="font-semibold flex items-center gap-2 text-amber-700">
                          <Coins className="h-4 w-4" />
                          Points
                        </h4>
                        
                        {/* Expiration points */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Activer l'expiration des points</Label>
                            <p className="text-xs text-muted-foreground">Si désactivé: points valides indéfiniment</p>
                          </div>
                          <Switch
                            checked={settings.points_expiration_enabled || false}
                            onCheckedChange={(checked) => {
                              setSettings(prev => ({ ...prev, points_expiration_enabled: checked }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                        
                        {settings.points_expiration_enabled && (
                          <div className="space-y-1 pl-4 border-l-2 border-amber-200">
                            <Label className="text-xs">Durée de validité (mois)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={settings.points_expiry_months || 0}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, points_expiry_months: parseInt(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                              className="w-32"
                            />
                            <p className="text-xs text-muted-foreground">0 = pas d'expiration</p>
                          </div>
                        )}
                        
                        {/* Limite max par transaction */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs">Max points par transaction</Label>
                            <Input
                              type="number"
                              min="0"
                              value={settings.max_points_per_transaction || 0}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, max_points_per_transaction: parseInt(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                            />
                            <p className="text-xs text-muted-foreground">0 = illimité</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Max remise par transaction (€)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={settings.max_discount_per_transaction || 0}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, max_discount_per_transaction: parseFloat(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                            />
                            <p className="text-xs text-muted-foreground">0 = illimité</p>
                          </div>
                        </div>
                      </div>

                      {/* Résumé des paramètres actifs */}
                      <div className="p-3 bg-slate-100 rounded-lg">
                        <p className="text-sm font-medium mb-2">Configuration actuelle:</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            {settings.promo_expiration_enabled ? (
                              <span className="text-amber-600">⏰ Promos expirent après {settings.default_promo_validity_days || 0}j</span>
                            ) : (
                              <span className="text-green-600">✓ Promos sans expiration</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {settings.promo_usage_limit_enabled ? (
                              <span className="text-amber-600">🔢 Promos limitées à {settings.default_promo_max_uses || 0} utilisations</span>
                            ) : (
                              <span className="text-green-600">✓ Promos illimitées</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {settings.points_expiration_enabled ? (
                              <span className="text-amber-600">⏰ Points expirent après {settings.points_expiry_months || 0} mois</span>
                            ) : (
                              <span className="text-green-600">✓ Points sans expiration</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {settings.max_discount_per_transaction > 0 ? (
                              <span className="text-amber-600">💰 Max {settings.max_discount_per_transaction}€ par transaction</span>
                            ) : (
                              <span className="text-green-600">✓ Pas de limite de remise</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Règles par Catégorie */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Plafonds par Catégorie
                  </CardTitle>
                  <CardDescription>
                    Protégez vos marges en limitant les remises par type de produit
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Réparation */}
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-blue-100 text-blue-700">Réparation</Badge>
                        <Badge variant="outline" className="text-green-600">Gagne des points</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Remise max (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={settings?.category_rules?.reparation?.max_discount_fixed || 10}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                category_rules: {
                                  ...prev.category_rules,
                                  reparation: {
                                    ...prev.category_rules?.reparation,
                                    max_discount_fixed: parseFloat(e.target.value)
                                  }
                                }
                              }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Remise max (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={settings?.category_rules?.reparation?.max_discount_percent || 10}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                category_rules: {
                                  ...prev.category_rules,
                                  reparation: {
                                    ...prev.category_rules?.reparation,
                                    max_discount_percent: parseFloat(e.target.value)
                                  }
                                }
                              }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Accessoires */}
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-green-100 text-green-700">Accessoires</Badge>
                        <Badge variant="outline" className="text-green-600">Gagne des points</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Remise max (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Illimité"
                            value={settings?.category_rules?.accessoire?.max_discount_fixed || ''}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                category_rules: {
                                  ...prev.category_rules,
                                  accessoire: {
                                    ...prev.category_rules?.accessoire,
                                    max_discount_fixed: e.target.value ? parseFloat(e.target.value) : null
                                  }
                                }
                              }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Remise max (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={settings?.category_rules?.accessoire?.max_discount_percent || 50}
                            onChange={(e) => {
                              setSettings(prev => ({
                                ...prev,
                                category_rules: {
                                  ...prev.category_rules,
                                  accessoire: {
                                    ...prev.category_rules?.accessoire,
                                    max_discount_percent: parseFloat(e.target.value)
                                  }
                                }
                              }));
                              setSettingsChanged(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vente dématérialisée */}
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50/50">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-gray-100 text-gray-500">Vente dématérialisée</Badge>
                        <Badge variant="outline" className="text-red-600">Exclu du programme</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Cette catégorie ne gagne pas de points et n'est pas éligible aux remises.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Anciens paramètres (fidélité classique) */}
              <Card>
                <CardHeader>
                  <CardTitle>Paramètres du Programme Fidélité</CardTitle>
                  <CardDescription>
                    Configurez les règles automatiques de fidélisation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {settings && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h3 className="font-semibold flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Seuil de Fidélité
                          </h3>
                          <div className="space-y-2">
                            <Label>Nombre de visites pour devenir fidèle</Label>
                            <Input
                              type="number"
                              min="1"
                              value={settings.visits_threshold}
                              onChange={(e) => {
                                setSettings(prev => ({ ...prev, visits_threshold: parseInt(e.target.value) }));
                                setSettingsChanged(true);
                              }}
                            />
                            <p className="text-sm text-muted-foreground">
                              Le client devient fidèle après {settings.visits_threshold} visite(s)
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold flex items-center gap-2">
                            <Gift className="h-4 w-4" />
                            Promotion Automatique
                          </h3>
                          <div className="flex items-center justify-between">
                            <Label>Créer une promo automatiquement</Label>
                            <Switch
                              checked={settings.auto_promo_enabled}
                              onCheckedChange={(checked) => {
                                setSettings(prev => ({ ...prev, auto_promo_enabled: checked }));
                                setSettingsChanged(true);
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Remise par défaut (%)</Label>
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                value={settings.default_discount_percent}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, default_discount_percent: parseFloat(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Validité (jours)</Label>
                              <Input
                                type="number"
                                min="1"
                                value={settings.promo_validity_days}
                                onChange={(e) => {
                                  setSettings(prev => ({ ...prev, promo_validity_days: parseInt(e.target.value) }));
                                  setSettingsChanged(true);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h3 className="font-semibold flex items-center gap-2 mb-4">
                          <MessageSquare className="h-4 w-4" />
                          Envoi Automatique
                        </h3>
                        <div className="flex gap-8">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={settings.send_sms}
                              onCheckedChange={(checked) => {
                                setSettings(prev => ({ ...prev, send_sms: checked }));
                                setSettingsChanged(true);
                              }}
                            />
                            <Label className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              Envoyer par SMS
                            </Label>
                          </div>
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={settings.send_email}
                              onCheckedChange={(checked) => {
                                setSettings(prev => ({ ...prev, send_email: checked }));
                                setSettingsChanged(true);
                              }}
                            />
                            <Label className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Envoyer par Email
                            </Label>
                          </div>
                        </div>
                      </div>

                      {settingsChanged && (
                        <div className="flex justify-end pt-4 border-t">
                          <Button onClick={handleSaveSettings} className="gap-2">
                            <Check className="h-4 w-4" />
                            Sauvegarder les paramètres
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Admin Tab - Audit & Nettoyage */}
          <TabsContent value="admin" className="space-y-6">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-bold text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Zone Administration
              </h3>
              <p className="text-sm text-red-600">Ces outils permettent d'auditer et nettoyer les données clients. Utilisez avec précaution.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Audit Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-blue-600" />
                    Audit des Clients
                  </CardTitle>
                  <CardDescription>Analyser les données pour détecter les anomalies</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    onClick={runAudit} 
                    disabled={auditLoading}
                    className="w-full"
                    data-testid="btn-run-audit"
                  >
                    {auditLoading ? 'Analyse en cours...' : 'Lancer l\'audit'}
                  </Button>

                  {auditData && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-lg text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-slate-500">Total clients:</span>
                          <span className="font-bold ml-2">{auditData.total_clients?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Avec points:</span>
                          <span className="font-bold ml-2">{auditData.clients_with_points?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Total points:</span>
                          <span className="font-bold ml-2">{auditData.total_points?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Dette virtuelle:</span>
                          <span className="font-bold ml-2 text-red-600">{auditData.debt_value_eur?.toLocaleString()} €</span>
                        </div>
                      </div>
                      
                      <div className="border-t pt-2">
                        <p className="font-medium text-red-600">Doublons détectés:</p>
                        <p>Par nom: <strong>{auditData.duplicates?.by_name || 0}</strong> groupes</p>
                        <p>Par téléphone: <strong>{auditData.duplicates?.by_phone || 0}</strong> groupes</p>
                      </div>

                      {auditData.sources && (
                        <div className="border-t pt-2">
                          <p className="font-medium">Sources des clients:</p>
                          {Object.entries(auditData.sources).map(([source, count]) => (
                            <p key={source} className="text-xs">{source}: <strong>{count}</strong></p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recalcul des Points */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-green-600" />
                    Recalcul des Points
                  </CardTitle>
                  <CardDescription>Recalculer les soldes depuis l'historique des transactions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-slate-600">
                    Corrige les soldes corrompus en recalculant depuis les transactions réelles (earn - redeem).
                  </p>
                  
                  <div className="space-y-2">
                    <Button 
                      onClick={() => runRecalculatePoints(true)}
                      disabled={recalculateLoading}
                      variant="outline"
                      className="w-full"
                      data-testid="btn-recalculate-preview"
                    >
                      {recalculateLoading ? 'Calcul...' : '1. Prévisualiser le recalcul'}
                    </Button>

                    {recalculateResult && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm space-y-2">
                        <p className="font-medium text-green-800">Résultat {recalculateResult.dry_run ? '(prévisualisation)' : '(appliqué)'} :</p>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <p>Clients analysés: <strong>{recalculateResult.total_clients}</strong></p>
                          <p>À corriger: <strong className="text-amber-600">{recalculateResult.clients_with_changes}</strong></p>
                          <p>Points avant: <strong>{recalculateResult.total_points_before?.toLocaleString()}</strong></p>
                          <p>Points après: <strong>{recalculateResult.total_points_after?.toLocaleString()}</strong></p>
                          <p>Dette avant: <strong className="text-red-600">{recalculateResult.debt_before_eur}€</strong></p>
                          <p>Dette après: <strong className="text-green-600">{recalculateResult.debt_after_eur}€</strong></p>
                        </div>
                        {recalculateResult.debt_saved_eur > 0 && (
                          <p className="text-green-700 font-bold">
                            Économie: {recalculateResult.debt_saved_eur}€
                          </p>
                        )}
                        {recalculateResult.corrections?.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-slate-600">
                              Voir {Math.min(recalculateResult.corrections.length, 10)} exemples de corrections
                            </summary>
                            <div className="mt-1 max-h-32 overflow-y-auto text-xs">
                              {recalculateResult.corrections.slice(0, 10).map((c, i) => (
                                <p key={i} className="py-0.5 border-b">
                                  {c.client_name}: {c.old_balance} → {c.new_balance} pts ({c.difference > 0 ? '+' : ''}{c.difference})
                                </p>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}

                    <Button 
                      onClick={() => runRecalculatePoints(false)}
                      disabled={recalculateLoading || !recalculateResult}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="btn-recalculate-apply"
                    >
                      2. Appliquer les corrections
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* RATTRAPAGE DES POINTS - Factures uniquement */}
              <Card className="border-blue-300 lg:col-span-2">
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Gift className="h-5 w-5" />
                    Rattrapage des Points (Factures)
                  </CardTitle>
                  <CardDescription className="text-blue-600">
                    Attribution rétroactive des points depuis les factures
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  <div className="text-xs text-slate-600 space-y-1 bg-slate-50 p-3 rounded">
                    <p><strong>Ce script calcule les points pour chaque client :</strong></p>
                    <p>• Utilise les <strong>FACTURES</strong> (invoices) comme source</p>
                    <p>• Lie les factures aux clients via <strong>client_id</strong> ou correspondance de nom</p>
                    <p>• Met à jour : points_balance, total_spent, loyalty_tier</p>
                    <p className="text-amber-600 font-medium mt-2">⚠️ À exécuter UNE SEULE FOIS après l'initialisation</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {/* Étape 1: Initialiser */}
                    <Button 
                      onClick={initLoyaltyFields}
                      disabled={initLoading}
                      variant="outline"
                      className="w-full border-amber-300 text-amber-700"
                      data-testid="btn-init-loyalty"
                    >
                      {initLoading ? 'Initialisation...' : '1. Initialiser à 0'}
                    </Button>
                    
                    {/* Étape 2: Prévisualiser */}
                    <Button 
                      onClick={() => runRattrapagePoints(true)}
                      disabled={rattrapageLoading}
                      variant="outline"
                      className="w-full border-blue-300"
                      data-testid="btn-rattrapage-preview"
                    >
                      {rattrapageLoading ? 'Calcul...' : '2. Prévisualiser'}
                    </Button>

                    {/* Étape 3: Appliquer */}
                    <Button 
                      onClick={() => runRattrapagePoints(false)}
                      disabled={rattrapageLoading || !rattrapageResult}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="btn-rattrapage-apply"
                    >
                      3. Appliquer
                    </Button>
                  </div>

                  {rattrapageResult && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-2">
                      <p className="font-medium text-blue-800">
                        {rattrapageResult.dry_run ? 'Prévisualisation' : 'Rattrapage effectué'} :
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <p>Total clients: <strong>{rattrapageResult.total_clients}</strong></p>
                        <p>Avec factures: <strong>{rattrapageResult.clients_with_invoices}</strong></p>
                        <p>À mettre à jour: <strong className="text-blue-600">{rattrapageResult.clients_updated}</strong></p>
                        <p>Total factures: <strong>{rattrapageResult.total_invoices}</strong></p>
                      </div>
                      <div className="border-t pt-2 text-xs grid grid-cols-2 gap-2">
                        <p>Montant total: <strong>{rattrapageResult.total_amount?.toLocaleString()}€</strong></p>
                        <p>Points après: <strong className="text-blue-700">{rattrapageResult.total_points_after?.toLocaleString()}</strong></p>
                      </div>
                      
                      {rattrapageResult.corrections?.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-slate-600">
                            Voir {Math.min(rattrapageResult.corrections.length, 20)} exemples
                          </summary>
                          <div className="mt-1 max-h-40 overflow-y-auto text-xs">
                            {rattrapageResult.corrections.slice(0, 20).map((c, i) => (
                              <p key={i} className="py-0.5 border-b">
                                {c.client_name}: {c.old_points} → <strong>{c.new_points}</strong> pts 
                                ({c.invoices_count} fact. = {c.total_amount}€) → {c.new_tier}
                              </p>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Cleanup Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-orange-600" />
                    Nettoyage Doublons
                  </CardTitle>
                  <CardDescription>Fusionner les clients en doublon</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Button 
                      onClick={runCleanupDryRun}
                      disabled={cleanupLoading}
                      variant="outline"
                      className="w-full"
                      data-testid="btn-cleanup-preview"
                    >
                      {cleanupLoading ? 'Analyse...' : '1. Prévisualiser le nettoyage'}
                    </Button>

                    {cleanupResult && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                        <p className="font-medium text-amber-800">Prévisualisation:</p>
                        <p>Groupes de doublons: <strong>{cleanupResult.duplicate_groups_found}</strong></p>
                        <p>Clients à supprimer: <strong className="text-red-600">{cleanupResult.clients_to_delete}</strong></p>
                        <p>Clients conservés: <strong className="text-green-600">{cleanupResult.clients_kept}</strong></p>
                        <p>Points fusionnés: <strong>{cleanupResult.points_merged}</strong></p>
                      </div>
                    )}

                    <Button 
                      onClick={runCleanupForReal}
                      disabled={cleanupLoading || !cleanupResult}
                      variant="destructive"
                      className="w-full"
                      data-testid="btn-cleanup-execute"
                    >
                      2. Exécuter le nettoyage
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Zone Dangereuse */}
              <Card className="border-red-300">
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    Zone Dangereuse
                  </CardTitle>
                  <CardDescription className="text-red-600">Actions irréversibles - authentification requise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  <Button 
                    onClick={runResetAll}
                    disabled={cleanupLoading}
                    variant="destructive"
                    className="w-full bg-red-800 hover:bg-red-900"
                    data-testid="btn-reset-total"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    RESET TOTAL (Code par email requis)
                  </Button>
                  
                  <div className="border-t pt-4">
                    <Button 
                      onClick={() => { loadBackups(); setShowBackupsModal(true); }}
                      variant="outline"
                      className="w-full"
                      data-testid="btn-show-backups"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Voir les Backups / Restaurer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Promo Modal */}
      <Dialog open={showNewPromoModal} onOpenChange={(open) => {
        setShowNewPromoModal(open);
        if (!open) setPromoClientSearch('');
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-violet-600" />
              Créer une Promotion
            </DialogTitle>
            <DialogDescription>
              Envoyez une offre personnalisée ou générale
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Type de promo: Individuelle ou Générale */}
            <div className="space-y-2">
              <Label>Type de promotion</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={newPromo.scope === 'individual' ? 'default' : 'outline'}
                  className="flex flex-col h-auto py-3"
                  onClick={() => setNewPromo(prev => ({ ...prev, scope: 'individual' }))}
                >
                  <User className="h-5 w-5 mb-1" />
                  <span className="font-medium">Individuelle</span>
                  <span className="text-xs opacity-80">Pour un client</span>
                </Button>
                <Button
                  type="button"
                  variant={newPromo.scope === 'general' ? 'default' : 'outline'}
                  className="flex flex-col h-auto py-3"
                  onClick={() => setNewPromo(prev => ({ ...prev, scope: 'general', client_id: '' }))}
                >
                  <Users className="h-5 w-5 mb-1" />
                  <span className="font-medium">Générale</span>
                  <span className="text-xs opacity-80">Tous les clients</span>
                </Button>
              </div>
            </div>

            {/* Recherche rapide client - seulement si individuelle */}
            {newPromo.scope === 'individual' && (
              <div className="space-y-2">
                <Label>Client * (recherche rapide)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom, téléphone ou email..."
                    value={promoClientSearch}
                    onChange={(e) => setPromoClientSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {/* Résultats de recherche */}
                {promoClientSearch.length >= 2 && filteredClientsForPromo.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto bg-white shadow-lg">
                    {filteredClientsForPromo.map((client) => (
                      <div 
                        key={client.id}
                        className={`p-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center ${newPromo.client_id === client.id ? 'bg-violet-50' : ''}`}
                        onClick={() => {
                          setNewPromo(prev => ({ ...prev, client_id: client.id }));
                          setPromoClientSearch('');
                        }}
                      >
                        <div>
                          <p className="font-medium text-sm">{client.name}</p>
                          <p className="text-xs text-muted-foreground">{client.phone || client.email}</p>
                        </div>
                        <Badge className="text-xs">{client.points_balance || 0} pts</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {/* Client sélectionné */}
                {newPromo.client_id && (
                  <div className="p-2 bg-violet-50 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sm">
                        {clients.find(c => c.id === newPromo.client_id)?.name || 'Client'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {clients.find(c => c.id === newPromo.client_id)?.phone || ''}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setNewPromo(prev => ({ ...prev, client_id: '' }))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de promo</Label>
                <Select 
                  value={newPromo.promo_type} 
                  onValueChange={(v) => setNewPromo(prev => ({ ...prev, promo_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Remise %</SelectItem>
                    <SelectItem value="fixed">Remise €</SelectItem>
                    <SelectItem value="points_conversion">Conversion de points</SelectItem>
                    <SelectItem value="gift">Carte cadeau</SelectItem>
                    <SelectItem value="free_repair">Réparation gratuite</SelectItem>
                    <SelectItem value="free_purchase">Achat gratuit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  {newPromo.promo_type === 'points_conversion' ? 'Points à convertir' : 'Valeur'}
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromo.value}
                  onChange={(e) => setNewPromo(prev => ({ ...prev, value: parseFloat(e.target.value) }))}
                />
              </div>
            </div>

            {/* Info conversion de points */}
            {newPromo.promo_type === 'points_conversion' && (
              <div className="p-3 bg-amber-50 rounded-lg text-sm">
                <p className="flex items-center gap-2 text-amber-700">
                  <Coins className="h-4 w-4" />
                  Conversion : {newPromo.value || 0} points = {((newPromo.value || 0) / (settings?.tier_fixed_points || 100) * (settings?.tier_fixed_value || 5)).toFixed(2)}€
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Basé sur le palier : {settings?.tier_fixed_points || 100} pts = {settings?.tier_fixed_value || 5}€
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Validité (jours)</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromo.validity_days}
                  onChange={(e) => setNewPromo(prev => ({ ...prev, validity_days: parseInt(e.target.value) }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Max utilisations</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo(prev => ({ ...prev, max_uses: parseInt(e.target.value) }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                Description (optionnel)
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAIPromoModal(true)}
                  className="h-7 px-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                  title="Générer avec l'IA"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Générer IA
                </Button>
              </Label>
              <Textarea
                placeholder="Message personnalisé..."
                value={newPromo.description}
                onChange={(e) => setNewPromo(prev => ({ ...prev, description: e.target.value }))}
              />
              {aiPromoApproved && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Texte généré par IA appliqué
                </p>
              )}
            </div>

            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newPromo.send_sms}
                  onCheckedChange={(checked) => setNewPromo(prev => ({ ...prev, send_sms: checked }))}
                />
                <Label className="flex items-center gap-1">
                  <Phone className="h-4 w-4" /> SMS
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newPromo.send_email}
                  onCheckedChange={(checked) => setNewPromo(prev => ({ ...prev, send_email: checked }))}
                />
                <Label className="flex items-center gap-1">
                  <Mail className="h-4 w-4" /> Email
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPromoModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreatePromo} className="gap-2">
              <Gift className="h-4 w-4" />
              Créer & Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-600" />
              Campagne Promotionnelle
            </DialogTitle>
            <DialogDescription>
              Envoyez une offre à tous vos clients fidèles
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cibler les clients</Label>
              <Select 
                value={campaign.tier} 
                onValueChange={(v) => setCampaign(prev => ({ ...prev, tier: v, selectedClients: v === 'selected' ? prev.selectedClients : [] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les clients ({dashboard?.clients?.total || 0})</SelectItem>
                  <SelectItem value="bronze">Bronze ({dashboard?.clients?.by_tier?.bronze || 0})</SelectItem>
                  <SelectItem value="silver">Silver ({dashboard?.clients?.by_tier?.silver || 0})</SelectItem>
                  <SelectItem value="gold">Gold ({dashboard?.clients?.by_tier?.gold || 0})</SelectItem>
                  <SelectItem value="selected">Sélection manuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sélection manuelle de clients */}
            {campaign.tier === 'selected' && (
              <div className="space-y-2 p-3 bg-violet-50 rounded-lg border border-violet-200">
                <Label>Sélectionner les clients</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par nom ou téléphone..."
                    value={campaignClientSearch}
                    onChange={(e) => setCampaignClientSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {/* Résultats de recherche */}
                {campaignClientSearch.length >= 2 && (
                  <div className="border rounded-lg max-h-32 overflow-y-auto bg-white">
                    {clients
                      .filter(c => 
                        c.name?.toLowerCase().includes(campaignClientSearch.toLowerCase()) ||
                        c.phone?.includes(campaignClientSearch)
                      )
                      .filter(c => !campaign.selectedClients.find(sc => sc.id === c.id))
                      .slice(0, 10)
                      .map(client => (
                        <div 
                          key={client.id}
                          className="p-2 hover:bg-slate-50 cursor-pointer flex justify-between items-center border-b last:border-0"
                          onClick={() => {
                            setCampaign(prev => ({
                              ...prev,
                              selectedClients: [...prev.selectedClients, client]
                            }));
                            setCampaignClientSearch('');
                          }}
                        >
                          <div>
                            <p className="text-sm font-medium">{client.name}</p>
                            <p className="text-xs text-muted-foreground">{client.phone || client.email}</p>
                          </div>
                          <Plus className="h-4 w-4 text-violet-600" />
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* Clients sélectionnés */}
                {campaign.selectedClients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {campaign.selectedClients.map(client => (
                      <Badge 
                        key={client.id} 
                        className="bg-violet-100 text-violet-700 pr-1 flex items-center gap-1"
                      >
                        {client.name}
                        <button
                          onClick={() => setCampaign(prev => ({
                            ...prev,
                            selectedClients: prev.selectedClients.filter(c => c.id !== client.id)
                          }))}
                          className="ml-1 hover:bg-violet-200 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {campaign.selectedClients.length} client(s) sélectionné(s)
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de promo</Label>
                <Select 
                  value={campaign.promo_type} 
                  onValueChange={(v) => setCampaign(prev => ({ ...prev, promo_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Remise %</SelectItem>
                    <SelectItem value="fixed">Remise €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valeur</Label>
                <Input
                  type="number"
                  min="1"
                  value={campaign.value}
                  onChange={(e) => setCampaign(prev => ({ ...prev, value: parseFloat(e.target.value) }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Message personnalisé (optionnel)</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateCampaignAIMessage}
                  disabled={campaignAIGenerating}
                  className="gap-1 text-violet-600 border-violet-300 hover:bg-violet-50"
                >
                  {campaignAIGenerating ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Générer avec IA
                </Button>
              </div>
              <Textarea
                placeholder="Offre spéciale pour nos clients fidèles..."
                value={campaign.message}
                onChange={(e) => setCampaign(prev => ({ ...prev, message: e.target.value }))}
              />
              
              {/* Résultat IA */}
              {campaignAIResult && (
                <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-violet-700">Suggestions IA :</p>
                  <div className="space-y-2">
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-slate-500 mb-1">Version SMS (court) :</p>
                      <p className="text-sm">{campaignAIResult.sms_version}</p>
                      <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs" onClick={() => applyCampaignAIMessage('sms')}>
                        <Check className="h-3 w-3 mr-1" /> Utiliser
                      </Button>
                    </div>
                    <div className="p-2 bg-white rounded border">
                      <p className="text-xs text-slate-500 mb-1">Version Email :</p>
                      <p className="text-sm">{campaignAIResult.email_version}</p>
                      <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs" onClick={() => applyCampaignAIMessage('email')}>
                        <Check className="h-3 w-3 mr-1" /> Utiliser
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={campaign.send_sms}
                  onCheckedChange={(checked) => setCampaign(prev => ({ ...prev, send_sms: checked }))}
                />
                <Label className="flex items-center gap-1">
                  <Phone className="h-4 w-4" /> SMS
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={campaign.send_email}
                  onCheckedChange={(checked) => setCampaign(prev => ({ ...prev, send_email: checked }))}
                />
                <Label className="flex items-center gap-1">
                  <Mail className="h-4 w-4" /> Email
                </Label>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Les notifications seront envoyées aux clients qui ont un email ou téléphone.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendCampaign} 
              className="gap-2"
              disabled={campaign.tier === 'selected' && campaign.selectedClients.length === 0}
            >
              <Send className="h-4 w-4" />
              Envoyer la Campagne
              {campaign.tier === 'selected' && campaign.selectedClients.length > 0 && (
                <span>({campaign.selectedClients.length})</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client History Modal */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>
          
          {clientHistory && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{clientHistory.stats.total_visits}</p>
                  <p className="text-sm text-muted-foreground">Visites</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{clientHistory.stats.total_promos}</p>
                  <p className="text-sm text-muted-foreground">Promos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold">{clientHistory.stats.used_promos}</p>
                  <p className="text-sm text-muted-foreground">Utilisées</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  {getTierBadge(clientHistory.stats.tier)}
                </div>
              </div>

              {/* Contact */}
              <div className="flex gap-4">
                {clientHistory.client.phone && (
                  <Badge variant="outline" className="gap-1">
                    <Phone className="h-3 w-3" /> {clientHistory.client.phone}
                  </Badge>
                )}
                {clientHistory.client.email && (
                  <Badge variant="outline" className="gap-1">
                    <Mail className="h-3 w-3" /> {clientHistory.client.email}
                  </Badge>
                )}
              </div>

              {/* Visits */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Historique des visites
                </h4>
                {clientHistory.visits?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {clientHistory.visits.slice(0, 10).map((visit, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                        <span className="capitalize">{visit.source}</span>
                        <span className="text-muted-foreground">
                          {new Date(visit.date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune visite enregistrée</p>
                )}
              </div>

              {/* Promos */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Gift className="h-4 w-4" />
                  Promotions
                </h4>
                {clientHistory.promos?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {clientHistory.promos.map((promo) => (
                      <div key={promo.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <div>
                          <span className="font-mono font-bold">{promo.code}</span>
                          <span className="text-muted-foreground ml-2">
                            {promo.value}{promo.promo_type === 'percent' ? '%' : '€'}
                          </span>
                        </div>
                        {getPromoStatusBadge(promo.status)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune promotion</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Promo Card Modal */}
      <Dialog open={showPromoCardModal} onOpenChange={setShowPromoCardModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Carte Fidélité
            </DialogTitle>
          </DialogHeader>
          
          <div 
            className="border rounded-lg p-4 bg-white"
            dangerouslySetInnerHTML={{ __html: promoCardHtml }}
          />

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleViewPromoCard(selectedPromo, 'ticket')}
            >
              Format Ticket
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleViewPromoCard(selectedPromo, 'a6')}
            >
              Format A6
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleViewPromoCard(selectedPromo, 'digital')}
            >
              Digital
            </Button>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html>
                    <head><title>Carte Fidélité</title></head>
                    <body style="margin:0;padding:20px;">${promoCardHtml}</body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.print();
              }}
              className="gap-2"
            >
              <Printer className="h-4 w-4" />
              Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Points Modal */}
      <Dialog open={showPointsModal} onOpenChange={setShowPointsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-600" />
              Points Fidélité - {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>
          
          {clientPoints && (
            <div className="space-y-6">
              {/* Solde actuel */}
              <div className="text-center p-6 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl">
                <p className="text-sm text-amber-700 mb-2">Solde actuel</p>
                <p className="text-5xl font-bold text-amber-600">{clientPoints.points_balance}</p>
                <p className="text-sm text-amber-600 mt-2">points</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{clientPoints.total_earned}</p>
                  <p className="text-sm text-green-600">Total gagnés</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{clientPoints.total_used}</p>
                  <p className="text-sm text-blue-600">Total utilisés</p>
                </div>
              </div>

              {/* RGPD Consent */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4" />
                  Consentement RGPD
                </h4>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Recevoir des offres par SMS
                    </Label>
                    <Switch
                      checked={clientPoints.gdpr_consent?.sms_marketing || false}
                      onCheckedChange={(checked) => {
                        handleUpdateGDPR(
                          selectedClient.id,
                          checked,
                          clientPoints.gdpr_consent?.email_marketing || false
                        );
                        setClientPoints(prev => ({
                          ...prev,
                          gdpr_consent: { ...prev.gdpr_consent, sms_marketing: checked }
                        }));
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Recevoir des offres par Email
                    </Label>
                    <Switch
                      checked={clientPoints.gdpr_consent?.email_marketing || false}
                      onCheckedChange={(checked) => {
                        handleUpdateGDPR(
                          selectedClient.id,
                          clientPoints.gdpr_consent?.sms_marketing || false,
                          checked
                        );
                        setClientPoints(prev => ({
                          ...prev,
                          gdpr_consent: { ...prev.gdpr_consent, email_marketing: checked }
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Historique récent - DÉTAILLÉ */}
              {clientPoints.history?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Historique des transactions
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientPoints.history.map((tx) => {
                      // Déterminer le type de source
                      const getSourceInfo = () => {
                        const refType = tx.reference_type || tx.source || tx.transaction_type;
                        switch(refType) {
                          case 'invoice': return { icon: '📄', label: 'Facture', color: 'bg-blue-100 text-blue-700' };
                          case 'entry': return { icon: '💵', label: 'Encaissement', color: 'bg-green-100 text-green-700' };
                          case 'storage': case 'bon_entree': return { icon: '🔧', label: 'Bon d\'entrée', color: 'bg-purple-100 text-purple-700' };
                          case 'order': return { icon: '📦', label: 'Commande', color: 'bg-orange-100 text-orange-700' };
                          case 'welcome_bonus': return { icon: '🎉', label: 'Bienvenue', color: 'bg-pink-100 text-pink-700' };
                          case 'manual_action': case 'manual': return { icon: '👤', label: 'Manuel (Admin)', color: 'bg-amber-100 text-amber-700' };
                          case 'redeem': return { icon: '🎁', label: 'Utilisation', color: 'bg-red-100 text-red-700' };
                          case 'adjust': return { icon: '⚙️', label: 'Ajustement', color: 'bg-slate-100 text-slate-700' };
                          default: return { icon: '📝', label: refType || 'Transaction', color: 'bg-gray-100 text-gray-700' };
                        }
                      };
                      const sourceInfo = getSourceInfo();
                      const isEarn = tx.transaction_type === 'earn' || tx.points > 0;
                      
                      return (
                        <div key={tx.id} className={`p-3 rounded-lg border-l-4 ${isEarn ? 'bg-green-50 border-l-green-500' : 'bg-red-50 border-l-red-500'}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={sourceInfo.color}>
                                  {sourceInfo.icon} {sourceInfo.label}
                                </Badge>
                                {tx.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {PRODUCT_CATEGORIES[tx.category]?.label || tx.category}
                                  </Badge>
                                )}
                              </div>
                              {tx.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{tx.notes}</p>
                              )}
                              {tx.amount_ttc > 0 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Montant: <span className="font-medium">{tx.amount_ttc.toFixed(2)}€</span>
                                </p>
                              )}
                              {tx.reference_id && (
                                <p className="text-xs text-slate-400 mt-0.5 font-mono">
                                  Réf: {tx.reference_id.substring(0, 12)}...
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-2">
                              <span className={`text-lg font-bold ${isEarn ? 'text-green-600' : 'text-red-600'}`}>
                                {isEarn ? '+' : ''}{tx.points} pts
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPointsModal(false);
                setShowAdjustPointsModal(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajuster
            </Button>
            <Button onClick={() => setShowPointsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Points Modal */}
      <Dialog open={showAdjustPointsModal} onOpenChange={setShowAdjustPointsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Ajuster les Points
            </DialogTitle>
            <DialogDescription>
              Ajouter ou retirer des points à {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setAdjustPoints(prev => ({ ...prev, points: prev.points - 10 }))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <Input
                  type="number"
                  value={adjustPoints.points}
                  onChange={(e) => setAdjustPoints(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                  className="w-32 text-center text-2xl font-bold"
                />
                <p className="text-sm text-muted-foreground mt-1">points</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setAdjustPoints(prev => ({ ...prev, points: prev.points + 10 }))}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Raison</Label>
              <Textarea
                placeholder="Ex: Geste commercial, correction d'erreur..."
                value={adjustPoints.notes}
                onChange={(e) => setAdjustPoints(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className={`p-3 rounded-lg text-center ${adjustPoints.points > 0 ? 'bg-green-100 text-green-700' : adjustPoints.points < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100'}`}>
              {adjustPoints.points > 0 
                ? `+${adjustPoints.points} points seront ajoutés`
                : adjustPoints.points < 0 
                  ? `${adjustPoints.points} points seront retirés`
                  : 'Aucun changement'
              }
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAdjustPointsModal(false);
              setAdjustPoints({ points: 0, notes: '' });
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleAdjustPoints}
              disabled={adjustPoints.points === 0}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== BLOC A - MODALS GESTION CLIENTS ===== */}

      {/* Modal Créer un nouveau client */}
      <Dialog open={showCreateClientModal} onOpenChange={setShowCreateClientModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Nouveau Client
            </DialogTitle>
            <DialogDescription>
              Créer un nouveau client dans le programme de fidélité
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                placeholder="Nom du client"
                value={newClient.name}
                onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-client-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={newClient.phone}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Statut fidélité</Label>
              <Select 
                value={newClient.loyalty_tier} 
                onValueChange={(v) => setNewClient(prev => ({ ...prev, loyalty_tier: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes ou remarques sur le client..."
                value={newClient.notes}
                onChange={(e) => setNewClient(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Consentement RGPD</Label>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newClient.sms_marketing}
                    onCheckedChange={(checked) => setNewClient(prev => ({ ...prev, sms_marketing: checked }))}
                  />
                  <span className="text-sm flex items-center gap-1"><Phone className="h-3 w-3" /> SMS OK</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={newClient.email_marketing}
                    onCheckedChange={(checked) => setNewClient(prev => ({ ...prev, email_marketing: checked }))}
                  />
                  <span className="text-sm flex items-center gap-1"><Mail className="h-3 w-3" /> Email OK</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateClientModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateClient} className="gap-2" data-testid="create-client-submit">
              <UserPlus className="h-4 w-4" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Éditer un client */}
      <Dialog open={showEditClientModal} onOpenChange={setShowEditClientModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Modifier le client
            </DialogTitle>
            <DialogDescription>
              {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                value={editClient.name}
                onChange={(e) => setEditClient(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  value={editClient.phone}
                  onChange={(e) => setEditClient(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editClient.email}
                  onChange={(e) => setEditClient(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Statut fidélité</Label>
              <Select 
                value={editClient.loyalty_tier} 
                onValueChange={(v) => setEditClient(prev => ({ ...prev, loyalty_tier: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editClient.notes}
                onChange={(e) => setEditClient(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Consentement RGPD</Label>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={editClient.sms_marketing}
                    onCheckedChange={(checked) => setEditClient(prev => ({ ...prev, sms_marketing: checked }))}
                  />
                  <span className="text-sm flex items-center gap-1"><Phone className="h-3 w-3" /> SMS OK</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={editClient.email_marketing}
                    onCheckedChange={(checked) => setEditClient(prev => ({ ...prev, email_marketing: checked }))}
                  />
                  <span className="text-sm flex items-center gap-1"><Mail className="h-3 w-3" /> Email OK</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-orange-600 flex items-center gap-1">
                <FileText className="h-4 w-4" />
                Raison de la modification *
              </Label>
              <Textarea
                placeholder="Pourquoi modifiez-vous ce client ? (obligatoire)"
                value={editClient.reason}
                onChange={(e) => setEditClient(prev => ({ ...prev, reason: e.target.value }))}
                className="border-orange-200 focus:border-orange-400"
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              className="text-red-600 hover:bg-red-50 border-red-200"
              onClick={() => handleDeleteClient(selectedClient?.id, true)}
              data-testid="delete-client-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditClientModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleEditClient} className="gap-2" disabled={!editClient.reason?.trim()}>
                <Save className="h-4 w-4" />
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Action manuelle */}
      <Dialog open={showManualActionModal} onOpenChange={setShowManualActionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-purple-600" />
              Action Manuelle
            </DialogTitle>
            <DialogDescription>
              Enregistrer un achat, réparation ou prestation pour {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'action</Label>
                <Select 
                  value={manualAction.action_type} 
                  onValueChange={(v) => setManualAction(prev => ({ ...prev, action_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Achat</SelectItem>
                    <SelectItem value="repair">Réparation</SelectItem>
                    <SelectItem value="service">Prestation</SelectItem>
                    <SelectItem value="visit">Visite simple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select 
                  value={manualAction.category} 
                  onValueChange={(v) => setManualAction(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reparation">Réparation</SelectItem>
                    <SelectItem value="accessoire">Accessoire</SelectItem>
                    <SelectItem value="occasion">Téléphone occasion</SelectItem>
                    <SelectItem value="neuf">Téléphone neuf</SelectItem>
                    <SelectItem value="dematerialise">Dématérialisé</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Montant TTC (€)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={manualAction.amount || ''}
                onChange={(e) => setManualAction(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              />
              {manualAction.amount > 0 && manualAction.category !== 'dematerialise' && (
                <p className="text-sm text-green-600">
                  ≈ +{Math.floor(manualAction.amount)} points à attribuer
                </p>
              )}
              {manualAction.category === 'dematerialise' && (
                <p className="text-sm text-orange-600">
                  Catégorie dématérialisée : pas de points
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Ex: Écran iPhone 13, coque silicone..."
                value={manualAction.description}
                onChange={(e) => setManualAction(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="flex gap-6 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={manualAction.add_visit}
                  onCheckedChange={(checked) => setManualAction(prev => ({ ...prev, add_visit: checked }))}
                />
                <span className="text-sm">Ajouter +1 visite</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={manualAction.calculate_points}
                  onCheckedChange={(checked) => setManualAction(prev => ({ ...prev, calculate_points: checked }))}
                />
                <span className="text-sm">Calculer les points</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-orange-600 flex items-center gap-1">
                <FileText className="h-4 w-4" />
                Raison / Justification *
              </Label>
              <Textarea
                placeholder="Pourquoi enregistrez-vous cette action manuellement ?"
                value={manualAction.reason}
                onChange={(e) => setManualAction(prev => ({ ...prev, reason: e.target.value }))}
                className="border-orange-200 focus:border-orange-400"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualActionModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleManualAction} 
              className="gap-2" 
              disabled={!manualAction.reason?.trim()}
              data-testid="submit-manual-action"
            >
              <Check className="h-4 w-4" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Historique des modifications */}
      <Dialog open={showClientHistoryModal} onOpenChange={setShowClientHistoryModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Historique des modifications
            </DialogTitle>
            <DialogDescription>
              {selectedClient?.name} - {clientModificationHistory.length} modification(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {clientModificationHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune modification enregistrée
              </p>
            ) : (
              clientModificationHistory.map((mod, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-lg border ${
                    mod.action === 'create' ? 'bg-green-50 border-green-200' :
                    mod.action === 'manual_action' ? 'bg-purple-50 border-purple-200' :
                    mod.action === 'status_change' ? 'bg-amber-50 border-amber-200' :
                    mod.action === 'visit_count_update' ? 'bg-blue-50 border-blue-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={
                      mod.action === 'create' ? 'bg-green-100 text-green-700' :
                      mod.action === 'manual_action' ? 'bg-purple-100 text-purple-700' :
                      mod.action === 'status_change' ? 'bg-amber-100 text-amber-700' :
                      mod.action === 'update' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {mod.action === 'create' && 'Création'}
                      {mod.action === 'update' && 'Modification'}
                      {mod.action === 'manual_action' && `Action: ${mod.action_type || 'N/A'}`}
                      {mod.action === 'status_change' && 'Changement statut'}
                      {mod.action === 'visit_count_update' && 'Nb visites modifié'}
                      {mod.action === 'archive' && 'Archivé'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(mod.date).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  
                  <p className="text-sm font-medium mb-2">
                    Raison : {mod.reason || 'Non spécifiée'}
                  </p>
                  
                  {mod.changes && Object.keys(mod.changes).length > 0 && (
                    <div className="text-xs space-y-1">
                      {Object.entries(mod.changes).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-muted-foreground">{key}:</span>
                          {typeof value === 'object' && value.from !== undefined ? (
                            <span>
                              <span className="text-red-600 line-through">{String(value.from || 'vide')}</span>
                              {' → '}
                              <span className="text-green-600">{String(value.to || 'vide')}</span>
                            </span>
                          ) : (
                            <span>{JSON.stringify(value)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {mod.details && (
                    <div className="text-xs mt-2 pt-2 border-t">
                      {mod.details.amount && <span className="mr-3">Montant: {mod.details.amount}€</span>}
                      {mod.details.points_earned > 0 && <span className="mr-3 text-green-600">+{mod.details.points_earned} pts</span>}
                      {mod.details.visit_added && <span className="text-blue-600">+1 visite</span>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowClientHistoryModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== BLOC B - Modal Promo de Masse ===== */}
      <Dialog open={showMassPromoModal} onOpenChange={setShowMassPromoModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Promotion Générale (Multi-clients)
            </DialogTitle>
            <DialogDescription>
              Appliquez une promotion à plusieurs clients en même temps
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filtre par statut */}
            <div className="flex gap-2 items-center">
              <Label>Filtrer par statut :</Label>
              <Select value={massPromoFilter} onValueChange={setMassPromoFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleMassPromoSelectAll}
              >
                {massPromoSelectAll ? 'Tout désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>

            {/* Liste des clients à sélectionner */}
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Sél.</th>
                    <th className="p-2 text-left">Client</th>
                    <th className="p-2 text-center">Points</th>
                    <th className="p-2 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {getMassPromoFilteredClients().map((client) => (
                    <tr 
                      key={client.id} 
                      className={`hover:bg-slate-50 cursor-pointer ${massPromoClients.includes(client.id) ? 'bg-purple-50' : ''}`}
                      onClick={() => toggleMassPromoClient(client.id)}
                    >
                      <td className="p-2">
                        <Checkbox 
                          checked={massPromoClients.includes(client.id)}
                          onCheckedChange={() => toggleMassPromoClient(client.id)}
                        />
                      </td>
                      <td className="p-2">
                        <p className="font-medium">{client.name}</p>
                        <p className="text-xs text-muted-foreground">{client.phone}</p>
                      </td>
                      <td className="p-2 text-center">
                        <Badge className="bg-amber-100 text-amber-700">{client.points_balance || 0}</Badge>
                      </td>
                      <td className="p-2 text-center">
                        {getTierBadge(client.loyalty_tier || 'standard')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {massPromoClients.length} client(s) sélectionné(s)
            </p>

            {/* Configuration de la promo */}
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de promo</Label>
                  <Select 
                    value={massPromo.promo_type} 
                    onValueChange={(v) => setMassPromo(prev => ({ ...prev, promo_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Remise %</SelectItem>
                      <SelectItem value="fixed">Remise €</SelectItem>
                      <SelectItem value="points_conversion">Conversion points</SelectItem>
                      <SelectItem value="gift">Carte cadeau</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valeur</Label>
                  <Input
                    type="number"
                    min="1"
                    value={massPromo.value}
                    onChange={(e) => setMassPromo(prev => ({ ...prev, value: parseFloat(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description / Message</Label>
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Message de la promotion..."
                    value={massPromo.description}
                    onChange={(e) => setMassPromo(prev => ({ ...prev, description: e.target.value }))}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setNewPromo(prev => ({ ...prev, ...massPromo }));
                      setShowAIPromoModal(true);
                    }}
                    className="shrink-0"
                    title="Générer avec IA"
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={massPromo.validity_days}
                    onChange={(e) => setMassPromo(prev => ({ ...prev, validity_days: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={massPromo.send_sms}
                      onCheckedChange={(checked) => setMassPromo(prev => ({ ...prev, send_sms: checked }))}
                    />
                    <Label className="flex items-center gap-1">
                      <Phone className="h-4 w-4" /> SMS
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={massPromo.send_email}
                      onCheckedChange={(checked) => setMassPromo(prev => ({ ...prev, send_email: checked }))}
                    />
                    <Label className="flex items-center gap-1">
                      <Mail className="h-4 w-4" /> Email
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMassPromoModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateMassPromo} 
              disabled={massPromoClients.length === 0}
              className="gap-2"
            >
              <Gift className="h-4 w-4" />
              Créer {massPromoClients.length} promo(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== BLOC C - Modal Génération IA Promo ===== */}
      <Dialog open={showAIPromoModal} onOpenChange={setShowAIPromoModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Génération IA du texte promo
            </DialogTitle>
            <DialogDescription>
              Cliquez sur "Générer auto" pour créer un texte basé sur la configuration, ou saisissez vos propres mots-clés
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Résumé de la config actuelle */}
            <div className="p-3 bg-slate-100 rounded-lg text-sm">
              <p className="font-medium mb-1">Configuration actuelle :</p>
              <p className="text-slate-600">
                • Type: {newPromo.promo_type === 'percent' ? 'Remise %' : newPromo.promo_type === 'fixed' ? 'Remise €' : newPromo.promo_type}
                <br/>• Valeur: {newPromo.value}{newPromo.promo_type === 'percent' ? '%' : '€'}
                <br/>• Cible: {newPromo.scope === 'general' ? 'Tous les clients' : 'Client individuel'}
              </p>
            </div>

            {/* Bouton génération automatique */}
            <Button 
              onClick={() => {
                setAiPromoKeywords(''); // Vider pour forcer la génération auto
                generateAIPromoText();
              }}
              disabled={aiPromoGenerating}
              className="w-full gap-2 bg-violet-600 hover:bg-violet-700"
            >
              {aiPromoGenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Générer automatiquement selon la config
                </>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">ou personnaliser</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Vos mots-clés (optionnel)</Label>
              <Textarea
                placeholder="Ex: soldes d'été, fidélité récompensée, nouveau client, anniversaire boutique..."
                value={aiPromoKeywords}
                onChange={(e) => setAiPromoKeywords(e.target.value)}
                rows={2}
              />
              <Button 
                onClick={generateAIPromoText}
                disabled={aiPromoGenerating || !aiPromoKeywords.trim()}
                variant="outline"
                className="w-full gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Générer avec mes mots-clés
              </Button>
            </div>

            {/* Résultat généré */}
            {aiPromoResult && (
              <div className="space-y-3">
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                  <Label className="text-violet-700 text-sm mb-2 block">Texte généré par l'IA :</Label>
                  <p className="text-violet-900 font-medium">{aiPromoResult}</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={generateAIPromoText}
                    className="flex-1 gap-2"
                    disabled={aiPromoGenerating}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Régénérer
                  </Button>
                  <Button
                    onClick={approveAIPromoText}
                    className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4" />
                    Approuver et appliquer
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIPromoModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== MODAL INFORMATION SYSTÈME ===== */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <BookOpen className="h-6 w-6 text-violet-600" />
              Guide du Programme Fidélité
            </DialogTitle>
            <DialogDescription>
              Tout comprendre sur le fonctionnement et le calcul des points
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Section 1: Calcul des points */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="font-bold text-amber-800 flex items-center gap-2 mb-3">
                <Coins className="h-5 w-5" />
                Calcul des Points
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="font-medium">Règle de base</span>
                  <Badge className="bg-amber-500 text-white text-lg px-3">1€ = 1 point</Badge>
                </div>
                <p className="text-amber-700">
                  Chaque euro dépensé par le client lui rapporte 1 point de fidélité.
                  Les points sont calculés sur le montant TTC de la vente.
                </p>
              </div>
            </div>

            {/* Section 2: Équivalence points/euros */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <h3 className="font-bold text-green-800 flex items-center gap-2 mb-3">
                <Euro className="h-5 w-5" />
                Équivalence Points → Euros
              </h3>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">100 pts</p>
                    <p className="text-green-700">=</p>
                    <p className="text-xl font-bold text-green-800">5€ de remise</p>
                    <p className="text-xs text-muted-foreground">(Palier fixe)</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">200 pts</p>
                    <p className="text-green-700">=</p>
                    <p className="text-xl font-bold text-green-800">10% de remise</p>
                    <p className="text-xs text-muted-foreground">(Palier %)</p>
                  </div>
                </div>
                <p className="text-green-700">
                  Le client choisit le palier qu'il souhaite utiliser lors du passage en caisse.
                </p>
              </div>
            </div>

            {/* Section 2bis: EXEMPLES CONCRETS */}
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
              <h3 className="font-bold text-indigo-800 flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5" />
                Exemples Concrets
              </h3>
              <div className="space-y-4 text-sm">
                
                {/* Exemple 1: Réparation */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-orange-400">
                  <p className="font-bold text-orange-700 mb-2">Exemple 1 : Réparation écran iPhone</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client fait réparer son écran iPhone 12 → <strong>89€ TTC</strong></p>
                    <p>• Points gagnés : 89€ × 1 = <span className="text-green-600 font-bold">+89 points</span></p>
                    <p>• Le client cumule 89 pts sur son compte fidélité</p>
                  </div>
                </div>

                {/* Exemple 2: Accessoire */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-purple-400">
                  <p className="font-bold text-purple-700 mb-2">Exemple 2 : Achat accessoire</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client achète une coque + chargeur → <strong>35€ TTC</strong></p>
                    <p>• Points gagnés : 35€ × 1 = <span className="text-green-600 font-bold">+35 points</span></p>
                    <p>• Total client : 89 + 35 = <strong>124 points</strong></p>
                  </div>
                </div>

                {/* Exemple 3: Utilisation des points */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-green-400">
                  <p className="font-bold text-green-700 mb-2">Exemple 3 : Utilisation des points</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client revient pour une réparation → <strong>79€ TTC</strong></p>
                    <p>• Il a <strong>124 points</strong> disponibles</p>
                    <p>• Option 1 : Utiliser 100 pts → <span className="text-green-600 font-bold">-5€</span> = paie 74€</p>
                    <p>• Option 2 : Garder ses points pour le palier 200 pts (10%)</p>
                    <p>• Après achat sans remise : 124 + 79 = <strong>203 points</strong></p>
                  </div>
                </div>

                {/* Exemple 4: Plafond réparation */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-amber-400">
                  <p className="font-bold text-amber-700 mb-2">Exemple 4 : Plafond de remise (Réparation)</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client a <strong>500 points</strong> et fait une réparation de <strong>150€</strong></p>
                    <p>• Théoriquement : 10% de 150€ = 15€ de remise</p>
                    <p>• <span className="text-orange-600 font-bold">MAIS</span> plafond réparation = 10€ max</p>
                    <p>• Remise appliquée : <span className="text-green-600 font-bold">-10€</span> (plafond)</p>
                  </div>
                </div>

                {/* Exemple 5: Dématérialisé exclu */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-red-400">
                  <p className="font-bold text-red-700 mb-2">Exemple 5 : Vente dématérialisée (EXCLU)</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client achète une recharge téléphonique → <strong>20€ TTC</strong></p>
                    <p>• Points gagnés : <span className="text-red-600 font-bold">0 point</span> (catégorie exclue)</p>
                    <p>• Le client ne peut PAS utiliser ses points sur cet achat</p>
                    <p className="text-xs text-red-500 mt-1">Les recharges, forfaits et produits dématérialisés sont exclus du programme.</p>
                  </div>
                </div>

                {/* Exemple 6: Téléphone occasion */}
                <div className="p-3 bg-white rounded-lg border-l-4 border-cyan-400">
                  <p className="font-bold text-cyan-700 mb-2">Exemple 6 : Vente téléphone occasion</p>
                  <div className="space-y-1 text-slate-700">
                    <p>• Client achète un iPhone 11 reconditionné → <strong>350€ TTC</strong></p>
                    <p>• Points gagnés : 350€ × 1 = <span className="text-green-600 font-bold">+350 points</span></p>
                    <p>• S'il utilise 200 pts pour 10% : remise = 35€</p>
                    <p>• <span className="text-orange-600 font-bold">MAIS</span> plafond occasion = 20€ max</p>
                    <p>• Remise appliquée : <span className="text-green-600 font-bold">-20€</span> (plafond)</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Section 3: Catégories de produits */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h3 className="font-bold text-blue-800 flex items-center gap-2 mb-3">
                <ShoppingBag className="h-5 w-5" />
                Règles par Catégorie
              </h3>
              <div className="space-y-2 text-sm">
                <div className="grid gap-2">
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="flex items-center gap-2">
                      <Badge className="bg-orange-100 text-orange-700">Réparation</Badge>
                    </span>
                    <span className="text-blue-700">Gagne des points • Max 10€ ou 10% remise</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700">Accessoires</Badge>
                    </span>
                    <span className="text-blue-700">Gagne des points • Jusqu'à 50% remise</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="flex items-center gap-2">
                      <Badge className="bg-cyan-100 text-cyan-700">Téléphone occasion</Badge>
                    </span>
                    <span className="text-blue-700">Gagne des points • Max 20€ remise</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <span className="flex items-center gap-2">
                      <Badge className="bg-red-100 text-red-700">Dématérialisé</Badge>
                    </span>
                    <span className="text-red-600 font-medium">EXCLU - 0 point, pas de remise</span>
                  </div>
                </div>
                <p className="text-blue-700 mt-2">
                  Les ventes dématérialisées (recharges, forfaits) ne génèrent pas de points et ne permettent pas d'utiliser de remise fidélité.
                </p>
              </div>
            </div>

            {/* Section 4: Niveaux de fidélité */}
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
              <h3 className="font-bold text-violet-800 flex items-center gap-2 mb-3">
                <Crown className="h-5 w-5" />
                Niveaux de Fidélité
              </h3>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-2 bg-white rounded-lg text-center">
                    <p className="font-bold text-slate-600">Standard</p>
                    <p className="text-xs text-muted-foreground">0-1 visite</p>
                  </div>
                  <div className="p-2 bg-amber-100 rounded-lg text-center border border-amber-300">
                    <p className="font-bold text-amber-700">Bronze</p>
                    <p className="text-xs text-amber-600">2+ visites</p>
                  </div>
                  <div className="p-2 bg-slate-200 rounded-lg text-center border border-slate-400">
                    <p className="font-bold text-slate-700">Silver</p>
                    <p className="text-xs text-slate-600">5+ visites</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg text-center border border-yellow-400">
                    <p className="font-bold text-yellow-700">Gold</p>
                    <p className="text-xs text-yellow-600">10+ visites</p>
                  </div>
                </div>
                <p className="text-violet-700 mt-2">
                  Le niveau évolue automatiquement en fonction du nombre de visites du client.
                </p>
              </div>
            </div>

            {/* Section 5: Onglets de l'interface */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3">
                <Info className="h-5 w-5" />
                Les Onglets
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 p-2 bg-white rounded-lg">
                  <TrendingUp className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Tableau de bord</p>
                    <p className="text-muted-foreground">Vue d'ensemble : total clients, points distribués/utilisés, statistiques par catégorie</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-white rounded-lg">
                  <Users className="h-5 w-5 text-violet-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Clients</p>
                    <p className="text-muted-foreground">Liste des clients avec leurs points, niveau et historique. Créer, modifier, attribuer des points manuellement</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-white rounded-lg">
                  <Ticket className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Promotions</p>
                    <p className="text-muted-foreground">Créer des promos individuelles ou de masse, envoyer par SMS/Email, générer le texte avec l'IA</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-white rounded-lg">
                  <Settings className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Paramètres</p>
                    <p className="text-muted-foreground">Configurer les paliers de conversion, plafonds par catégorie, seuil de visites pour devenir fidèle</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6: Intégration caisse */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <h3 className="font-bold text-emerald-800 flex items-center gap-2 mb-3">
                <Wallet className="h-5 w-5" />
                Intégration en Caisse
              </h3>
              <div className="space-y-2 text-sm text-emerald-700">
                <p><strong>Dans les encaissements :</strong> Chaque ligne a une icône 👤 pour attribuer les points au client.</p>
                <p><strong>Dans les factures :</strong> Lors de la création, le solde de points du client s'affiche et il peut les utiliser pour une remise.</p>
                <p className="flex items-center gap-2 p-2 bg-white rounded-lg">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>L'attribution de fidélité est <strong>obligatoire</strong> avant de valider une ligne d'encaissement.</span>
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowInfoModal(false)} className="gap-2">
              <Check className="h-4 w-4" />
              J'ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Relance Manuelle */}
      <Dialog open={showRelanceModal} onOpenChange={setShowRelanceModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-red-600" />
              Relance Client
            </DialogTitle>
            <DialogDescription>
              Envoyer un message personnalisé à {relanceClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          {relanceClient && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium">{relanceClient.name}</p>
                <p className="text-sm text-red-700">
                  {relanceClient.points_balance} pts • Inactif depuis {relanceClient.days_inactive} jours
                </p>
                <div className="flex gap-2 mt-2">
                  {relanceClient.has_email && relanceClient.gdpr_email && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      <Mail className="h-3 w-3 mr-1" /> {relanceClient.email}
                    </Badge>
                  )}
                  {relanceClient.has_phone && relanceClient.gdpr_sms && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <Phone className="h-3 w-3 mr-1" /> {relanceClient.phone}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Message personnalisé (optionnel)</Label>
                <Textarea
                  placeholder="Laissez vide pour utiliser le message par défaut..."
                  value={relanceMessage}
                  onChange={(e) => setRelanceMessage(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Message par défaut : "Bonjour [Nom], cela fait un moment... Vos [X] points vous attendent !"
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRelanceModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendRelance}
              disabled={sendingNotification}
              className="gap-2 bg-red-500 hover:bg-red-600"
            >
              <Send className="h-4 w-4" />
              {sendingNotification ? 'Envoi...' : 'Envoyer la relance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmation avant Envoi */}
      <Dialog open={showConfirmSendModal} onOpenChange={setShowConfirmSendModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirmation d'envoi
            </DialogTitle>
            <DialogDescription>
              {confirmSendType === 'near_threshold' && 'Notifier les clients proches du palier de récompense'}
              {confirmSendType === 'ready_to_redeem' && 'Notifier les clients avec remise disponible'}
              {confirmSendType === 'monthly_recap' && 'Envoyer le récapitulatif mensuel'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="font-medium text-amber-800 mb-2">
                {confirmSendClients.length} client(s) seront notifiés
              </p>
              <p className="text-sm text-amber-700">
                Seuls les clients ayant donné leur consentement RGPD (Email ou SMS) recevront la notification.
              </p>
            </div>

            {confirmSendClients.length > 0 && (
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Client</th>
                      <th className="text-center p-2 font-medium">Points</th>
                      <th className="text-center p-2 font-medium">Envoi via</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {confirmSendClients.slice(0, 20).map((client) => (
                      <tr key={client.id} className="hover:bg-slate-50">
                        <td className="p-2">{client.name}</td>
                        <td className="p-2 text-center">
                          <Badge className="bg-amber-100 text-amber-700">
                            {client.points_balance || 0} pts
                          </Badge>
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex justify-center gap-1">
                            {(client.gdpr_email || client.email_marketing || client.gdpr_consent?.email_marketing) && client.email && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                <Mail className="h-3 w-3" />
                              </Badge>
                            )}
                            {(client.gdpr_sms || client.sms_marketing || client.gdpr_consent?.sms_marketing) && client.phone && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <Phone className="h-3 w-3" />
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {confirmSendClients.length > 20 && (
                  <p className="text-center text-xs text-muted-foreground py-2 border-t">
                    ... et {confirmSendClients.length - 20} autres clients
                  </p>
                )}
              </div>
            )}

            {confirmSendClients.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <UserX className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucun client éligible avec consentement RGPD</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmSendModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={confirmAndSendNotifications}
              disabled={sendingNotification || confirmSendClients.length === 0}
              className="gap-2"
              data-testid="confirm-send-btn"
            >
              <Send className="h-4 w-4" />
              {sendingNotification ? 'Envoi en cours...' : `Confirmer l'envoi (${confirmSendClients.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Consommation de Points */}
      <Dialog open={showConsumePointsModal} onOpenChange={setShowConsumePointsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-green-600" />
              Utiliser les Points Fidélité
            </DialogTitle>
            <DialogDescription>
              Convertir les points de {consumePointsClient?.name} en remise
            </DialogDescription>
          </DialogHeader>
          
          {consumePointsClient && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-green-800">{consumePointsClient.name}</p>
                    <p className="text-sm text-green-700">Solde actuel</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800 text-xl font-bold px-4 py-2">
                    <Coins className="h-5 w-5 mr-2" />
                    {consumePointsClient.points_balance || 0} pts
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant de la vente (€) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={consumePointsData.sale_amount}
                    onChange={(e) => setConsumePointsData(prev => ({
                      ...prev,
                      sale_amount: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="Ex: 50.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={consumePointsData.category} 
                    onValueChange={(v) => setConsumePointsData(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRODUCT_CATEGORIES).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Type de remise</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={consumePointsData.redemption_type === 'fixed' ? 'default' : 'outline'}
                    className="flex flex-col h-auto py-3"
                    onClick={() => setConsumePointsData(prev => ({ ...prev, redemption_type: 'fixed' }))}
                  >
                    <Euro className="h-5 w-5 mb-1" />
                    <span className="font-medium">Remise Fixe</span>
                    <span className="text-xs opacity-80">
                      {settings?.tier_fixed_points || 100} pts = {settings?.tier_fixed_value || 5}€
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant={consumePointsData.redemption_type === 'percent' ? 'default' : 'outline'}
                    className="flex flex-col h-auto py-3"
                    onClick={() => setConsumePointsData(prev => ({ ...prev, redemption_type: 'percent' }))}
                    disabled={(consumePointsClient.points_balance || 0) < (settings?.tier_percent_points || 200)}
                  >
                    <Percent className="h-5 w-5 mb-1" />
                    <span className="font-medium">Remise %</span>
                    <span className="text-xs opacity-80">
                      {settings?.tier_percent_points || 200} pts = {settings?.tier_percent_value || 10}%
                    </span>
                  </Button>
                </div>
              </div>

              {consumePointsData.sale_amount > 0 && (
                <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-sm text-violet-700 mb-2">Aperçu de la remise :</p>
                  <div className="flex justify-between items-center">
                    <span className="text-violet-800">
                      {consumePointsData.redemption_type === 'fixed' 
                        ? `${settings?.tier_fixed_points || 100} points utilisés`
                        : `${settings?.tier_percent_points || 200} points utilisés`
                      }
                    </span>
                    <span className="text-xl font-bold text-violet-800">
                      -{consumePointsData.redemption_type === 'fixed' 
                        ? `${settings?.tier_fixed_value || 5}€`
                        : `${settings?.tier_percent_value || 10}% (${((consumePointsData.sale_amount * (settings?.tier_percent_value || 10)) / 100).toFixed(2)}€)`
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsumePointsModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleConsumePoints}
              disabled={!consumePointsData.sale_amount || consumePointsData.sale_amount <= 0}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="confirm-consume-points-btn"
            >
              <Check className="h-4 w-4" />
              Appliquer la remise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Création Avoir */}
      <Dialog open={showAvoirModal} onOpenChange={setShowAvoirModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-blue-600" />
              Créer un Avoir
            </DialogTitle>
            <DialogDescription>
              Suite à un remboursement ou annulation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select 
                value={newAvoir.client_id} 
                onValueChange={(v) => setNewAvoir(prev => ({ ...prev, client_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} ({client.points_balance || 0} pts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Montant de l'avoir (€) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newAvoir.amount}
                onChange={(e) => setNewAvoir(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                placeholder="Ex: 50.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Raison</Label>
              <Input
                type="text"
                value={newAvoir.reason}
                onChange={(e) => setNewAvoir(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Ex: Remboursement réparation annulée"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
              <Switch
                checked={newAvoir.deduct_points}
                onCheckedChange={(checked) => setNewAvoir(prev => ({ ...prev, deduct_points: checked }))}
              />
              <div>
                <Label className="text-red-700">Déduire les points correspondants</Label>
                <p className="text-xs text-red-600">
                  Si le client avait gagné des points sur cet achat, ils seront déduits automatiquement
                </p>
              </div>
            </div>

            {newAvoir.deduct_points && newAvoir.amount > 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">
                ≈ {Math.round(newAvoir.amount * (settings?.points_per_euro || 1))} points seront déduits du solde du client
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvoirModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateAvoir}
              disabled={!newAvoir.client_id || newAvoir.amount <= 0}
              className="gap-2"
            >
              <Check className="h-4 w-4" />
              Créer l'avoir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Validations en Attente (Doublons) */}
      <Dialog open={showPendingValidationsModal} onOpenChange={setShowPendingValidationsModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Transactions en attente de validation
            </DialogTitle>
            <DialogDescription>
              Ces transactions sont des doublons potentiels (même client + montant similaire).
              Validez ou rejetez chaque transaction.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {pendingValidations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-2" />
                <p>Aucune transaction en attente</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <Badge variant="outline" className="text-amber-600">
                    {pendingValidations.length} transaction(s) en attente
                  </Badge>
                  <Button
                    size="sm"
                    onClick={handleApproveAllValidations}
                    className="gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4" />
                    Tout approuver
                  </Button>
                </div>
                
                <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                  {pendingValidations.map((validation) => (
                    <div key={validation.id} className="p-4 hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <p className="font-medium">{validation.client_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {validation.source === 'invoice' && '📄 Facture'}
                            {validation.source === 'entry' && '💵 Encaissement'}
                            {validation.source === 'storage' && '🔧 Bon d\'entrée'}
                            {validation.source === 'order' && '📦 Commande'}
                            {' - '}{validation.description}
                          </p>
                          <div className="flex gap-2 text-sm">
                            <Badge variant="outline">{validation.amount}€</Badge>
                            <Badge className="bg-amber-100 text-amber-700">+{validation.points} pts</Badge>
                            {validation.transaction_date && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(validation.transaction_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectValidation(validation.id)}
                            disabled={processingValidation === validation.id}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveValidation(validation.id)}
                            disabled={processingValidation === validation.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {processingValidation === validation.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingValidationsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nettoyage Doublons de Points */}
      <Dialog open={showDuplicatesModal} onOpenChange={setShowDuplicatesModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Analyse des doublons de points
            </DialogTitle>
            <DialogDescription>
              Les doublons sont des transactions ayant le même client ET le même montant.
              Le nettoyage garde la première transaction et supprime les suivantes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {duplicatesAnalysis ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <div className="text-3xl font-bold text-amber-600">{duplicatesAnalysis.duplicate_groups}</div>
                    <div className="text-sm text-amber-700">Groupes de doublons</div>
                  </div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                    <div className="text-3xl font-bold text-red-600">{duplicatesAnalysis.total_excess_points}</div>
                    <div className="text-sm text-red-700">Points en excès à corriger</div>
                  </div>
                </div>
                
                {duplicatesAnalysis.duplicate_groups === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Check className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p className="text-green-600 font-medium">Aucun doublon détecté !</p>
                    <p className="text-sm">Les points sont correctement attribués.</p>
                  </div>
                ) : (
                  <>
                    <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                      {duplicatesAnalysis.duplicates?.slice(0, 10).map((dup, idx) => (
                        <div key={idx} className="p-3 hover:bg-slate-50">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{dup.client_name}</p>
                              <p className="text-sm text-muted-foreground">
                                Montant: {dup.amount}€ | {dup.count} transactions
                              </p>
                            </div>
                            <Badge variant="destructive">
                              -{dup.excess_points} pts
                            </Badge>
                          </div>
                          <div className="mt-2 text-xs space-y-1">
                            <div className="flex items-center gap-2 text-green-600">
                              <Check className="h-3 w-3" />
                              Garder: {dup.legitimate?.points} pts - {dup.legitimate?.notes?.slice(0, 40)}...
                            </div>
                            {dup.duplicates_to_remove?.map((d, i) => (
                              <div key={i} className="flex items-center gap-2 text-red-500">
                                <X className="h-3 w-3" />
                                Supprimer: {d.points} pts - {d.notes?.slice(0, 40)}...
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ Note importante:</strong> Le nettoyage conservera la première transaction de chaque groupe
                        (la plus ancienne) et supprimera les doublons. Les soldes des clients seront recalculés automatiquement.
                        Cette action est <strong>irréversible</strong>.
                      </p>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 mx-auto animate-spin text-slate-400" />
                <p className="text-sm text-muted-foreground mt-2">Analyse en cours...</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDuplicatesModal(false)}>
              Fermer
            </Button>
            {duplicatesAnalysis?.duplicate_groups > 0 && (
              <Button 
                variant="destructive" 
                onClick={handleCleanDuplicates}
                disabled={cleaningDuplicates}
                className="gap-2"
              >
                {cleaningDuplicates ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Nettoyer {duplicatesAnalysis.duplicate_groups} doublon(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal OTP pour Reset Total */}
      <Dialog open={showResetOtpModal} onOpenChange={setShowResetOtpModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Confirmation Reset Total
            </DialogTitle>
            <DialogDescription>
              Un code de sécurité sera envoyé par email pour autoriser cette action dangereuse.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!resetOtpSent ? (
              <>
                <div className="space-y-2">
                  <Label>Email de réception du code</Label>
                  <Select value={resetOtpEmail} onValueChange={setResetOtpEmail}>
                    <SelectTrigger data-testid="select-otp-email">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ouahcen397@yahoo.fr">ouahcen397@yahoo.fr</SelectItem>
                      <SelectItem value="kophonelab@gmail.com">kophonelab@gmail.com</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                  <p className="font-medium">Cette action va :</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>Créer un backup automatique</li>
                    <li>Supprimer TOUS les clients</li>
                    <li>Supprimer TOUTES les transactions de points</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Mail className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-green-700">Code envoyé à <strong>{resetOtpEmail}</strong></p>
                  <p className="text-xs text-green-600">Valide 10 minutes</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Code de confirmation (6 chiffres)</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={resetOtpCode}
                    onChange={(e) => setResetOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest font-mono"
                    data-testid="input-otp-code"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResetOtpModal(false)}>
              Annuler
            </Button>
            {!resetOtpSent ? (
              <Button 
                onClick={requestResetOtp} 
                disabled={resetOtpLoading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="btn-send-otp"
              >
                {resetOtpLoading ? 'Envoi...' : 'Envoyer le code'}
              </Button>
            ) : (
              <Button 
                onClick={executeResetWithOtp} 
                disabled={resetOtpLoading || resetOtpCode.length !== 6}
                variant="destructive"
                data-testid="btn-confirm-reset"
              >
                {resetOtpLoading ? 'Suppression...' : 'Confirmer la suppression'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Backups */}
      <Dialog open={showBackupsModal} onOpenChange={setShowBackupsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-600" />
              Backups Disponibles
            </DialogTitle>
            <DialogDescription>
              Restaurez un backup précédent ou supprimez les anciens
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {backupsLoading ? (
              <div className="text-center py-8 text-slate-500">Chargement...</div>
            ) : backups.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <RotateCcw className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>Aucun backup disponible</p>
                <p className="text-xs">Un backup est créé automatiquement avant chaque reset</p>
              </div>
            ) : (
              backups.map((backup) => (
                <div key={backup.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">
                        {new Date(backup.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {backup.clients_count} clients, {backup.transactions_count} transactions
                      </p>
                      <p className="text-xs text-slate-400">Raison: {backup.reason}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {backup.id.slice(0, 8)}...
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => restoreBackup(backup.id)}
                      disabled={backupsLoading}
                      className="flex-1"
                      data-testid={`btn-restore-${backup.id.slice(0,8)}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restaurer
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => deleteBackup(backup.id)}
                      disabled={backupsLoading}
                      data-testid={`btn-delete-backup-${backup.id.slice(0,8)}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackupsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
